import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { useLocation, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { usePilotMode } from "@/hooks/use-pilot-mode";
import { Settings, Save, Loader2, Globe, Building2, Users, Palette, Share2, Image, Phone, Mail, MapPin, Plus, X, Link2, Unlink, CheckCircle2, DollarSign, TrendingUp, AlertTriangle, Zap, Bot, Clock, CreditCard, Calendar, Sparkles, Code2, Copy, Key, Webhook, RefreshCw, Trash2, Check, ExternalLink, UserCog, ChevronDown, Pencil, Lock, Brain, ShieldOff, BarChart2, BarChart3, Info, MessageCircle, Eye, ToggleLeft, ToggleRight } from "lucide-react";
import type { DraperMemory, DraperRoutine, RoutineRun } from "@shared/schema";
import TeamPage from "@/pages/team";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandKitPuller, isBareDomainUrl } from "@/components/brand-kit-puller";
import { useAuth } from "@/hooks/use-auth";
import { SiLinkedin, SiX, SiInstagram, SiFacebook, SiTiktok, SiGoogle } from "react-icons/si";
import type { Workspace } from "@shared/schema";
import { AiEngineSetting } from "@/components/ai-engine-setting";
import { PublishPolicySetting } from "@/components/publish-policy-setting";
import { EmailSenderSetting } from "@/components/email-sender-setting";
import { EscalationSettings } from "@/components/escalation-settings";
import { WpConnectorDownload } from "@/components/wp-connector-download";
import { BusinessKnowledgeSetting } from "@/components/business-knowledge-setting";
import { getAiEngine, usesClaudeTeam } from "@/lib/ai-engine";

const SETTINGS_TABS = ["brand", "publishing", "agents", "notifications", "memory", "routines", "content-targeting", "personal-brand", "site-agent", "agency-branding"] as const;

const MemoryTab = lazy(() => import('./settings/MemoryTab'));
const RoutinesTab = lazy(() => import('./settings/RoutinesTab'));
const ContentTargetingTab = lazy(() => import('./settings/ContentTargetingTab'));
const PersonalBrandTab = lazy(() => import('./settings/PersonalBrandTab'));
const SiteAgentTab = lazy(() => import('./settings/SiteAgentTab'));
const AgencyBrandingTab = lazy(() => import('./settings/AgencyBrandingTab'));

function GscConnectButton({ workspaceId }: { workspaceId: number }) {

  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const res = await apiRequest("GET", `/api/workspaces/${workspaceId}/gsc/connect`);
      if (res.status === 501) {
        const err = await res.json();
        toast({ title: "GSC credentials not configured", description: err.error, variant: "destructive" });
        return;
      }
      const { authUrl } = await res.json();
      window.location.href = authUrl;
    } catch (e: any) {
      toast({ title: "Connect failed", description: e.message, variant: "destructive" });
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <Button
      data-testid="button-gsc-connect"
      onClick={handleConnect}
      disabled={isConnecting}
      className="flex items-center gap-2 bg-white text-gray-900 hover:bg-gray-100 border border-gray-300"
    >
      {isConnecting ? <Loader2 size={14} className="animate-spin" /> : <SiGoogle size={14} />}
      Connect Google Search Console
    </Button>
  );
}
type SettingsTab = (typeof SETTINGS_TABS)[number];
function isSettingsTab(value: string): value is SettingsTab {
  return (SETTINGS_TABS as readonly string[]).includes(value);
}

export default function SettingsPage() {
  const { activeWorkspaceId, setActiveWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const pilotMode = usePilotMode(); // PILOT_MODE: no subscription / plans / overage-billing sections
  const [, setLocation] = useLocation();
  const search = useSearch();
  const tabFromUrl = new URLSearchParams(search).get("tab") ?? "";
  const activeSettingsTab: SettingsTab = isSettingsTab(tabFromUrl) ? tabFromUrl : "brand";

  // A deep link such as /settings?tab=publishing#section-email-sender (Draper home's "Finish setup" card) scrolls to that
  // section once it has rendered (matched by id, else by data-testid).
  useEffect(() => {
    const target = decodeURIComponent((window.location.hash || "").slice(1));
    if (!target || !/^[\w-]+$/.test(target)) return;
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById(target) || document.querySelector(`[data-testid="${target}"]`);
      if (el || ++tries > 20) {
        clearInterval(timer);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
    return () => clearInterval(timer);
  }, [activeSettingsTab]);

  const { data: workspaces = [] } = useQuery<Workspace[]>({
    queryKey: ["/api/workspaces"],
  });

  const workspace = workspaces.find((w) => w.id === activeWorkspaceId);

  const { data: roleData } = useQuery<{ role: string }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "my-role"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { role: "owner" };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/my-role`, { credentials: "include" });
      if (!res.ok) return { role: "owner" };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });
  const isViewer = roleData?.role === "viewer";

  const { user } = useAuth();
  const [aiUsage, setAiUsage] = useState<any>(null);
  const [spendingLimit, setSpendingLimit] = useState<string>("");
  const [savingLimit, setSavingLimit] = useState(false);
  const [overageStatus, setOverageStatus] = useState<{
    overageBillingEnabled: boolean;
    hasCard: boolean;
    aiUsageBalance: number;
    threshold: number;
    chargeFailed: boolean;
  } | null>(null);
  const [togglingOverage, setTogglingOverage] = useState(false);
  const [settingUpCard, setSettingUpCard] = useState(false);

  const { data: budgetEnvelopes = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "ad-budget-envelopes"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/ad-budget-envelopes`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const [envelopeInputs, setEnvelopeInputs] = useState<Record<string, string>>({});

  const upsertEnvelope = useMutation({
    mutationFn: async ({ platform, weeklyLimitCents }: { platform: string; weeklyLimitCents: number }) => {
      const res = await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}/ad-budget-envelopes`, { platform, weeklyLimitCents });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "ad-budget-envelopes"] });
      toast({ title: "Budget envelope saved" });
    },
  });

  useEffect(() => {
    fetch("/api/my/ai-usage", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setAiUsage(data);
          setSpendingLimit(data.aiSpendingLimit?.toString() || "");
        }
      })
      .catch(() => {});

    fetch("/api/my/overage-status", { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setOverageStatus(data); })
      .catch(() => {});

    const params = new URLSearchParams(window.location.search);
    const overageSetup = params.get("overage_setup");
    const sessionId = params.get("session_id");
    if (overageSetup === "success" && sessionId) {
      window.history.replaceState({}, "", "/settings");
      fetch("/api/stripe/complete-overage-setup", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.success) {
            setOverageStatus(prev => prev ? { ...prev, overageBillingEnabled: true, hasCard: true } : null);
            toast({ title: "Overage billing activated", description: "Your card has been saved. AI usage overages will be charged at $10 intervals." });
            fetch("/api/my/overage-status", { credentials: "include" })
              .then(r => r.ok ? r.json() : null)
              .then(d => { if (d) setOverageStatus(d); })
              .catch(() => {});
          } else {
            toast({ title: "Card setup failed", description: data.message || "Could not save your card.", variant: "destructive" });
          }
        })
        .catch(() => toast({ title: "Card setup failed", variant: "destructive" }));
    } else if (overageSetup === "cancel") {
      window.history.replaceState({}, "", "/settings");
      toast({ title: "Card setup cancelled" });
    }
  }, []);

  async function toggleOverageBilling(enable: boolean) {
    if (enable && !overageStatus?.hasCard) {
      setSettingUpCard(true);
      try {
        const res = await fetch("/api/stripe/setup-payment-method", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        });
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast({ title: "Could not start card setup", description: data.message || "Please try again.", variant: "destructive" });
          setSettingUpCard(false);
        }
      } catch {
        toast({ title: "Card setup error", variant: "destructive" });
        setSettingUpCard(false);
      }
      return;
    }
    setTogglingOverage(true);
    try {
      const res = await fetch("/api/my/overage-billing", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: enable }),
      });
      const data = await res.json();
      if (res.ok) {
        setOverageStatus(prev => prev ? { ...prev, overageBillingEnabled: enable } : null);
        toast({ title: enable ? "Overage billing enabled" : "Overage billing disabled" });
      } else {
        toast({ title: "Failed to update", description: data.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Failed to update overage billing", variant: "destructive" });
    } finally {
      setTogglingOverage(false);
    }
  }

  const saveSpendingLimit = async () => {
    setSavingLimit(true);
    try {
      await fetch("/api/my/ai-spending-limit", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: spendingLimit ? Number(spendingLimit) : null }),
        credentials: "include",
      });
      toast({ title: "Spending limit updated" });
    } catch {
      toast({ title: "Failed to update limit", variant: "destructive" });
    } finally {
      setSavingLimit(false);
    }
  };

  const [form, setForm] = useState({
    name: "",
    brandName: "",
    url: "",
    industry: "",
    targetAudience: "",
    brandVoice: "",
    brandTone: "",
    logoUrl: "",
    brandColors: [] as string[],
    contactInfo: { phone: "", email: "", address: "", website: "", city: "" } as any,
    socialUrls: { linkedin: "", x: "", instagram: "", facebook: "", tiktok: "", website: "" } as Record<string, string>,
  });
  const [newColor, setNewColor] = useState("#3B82F6");

  const [autoGenConfig, setAutoGenConfig] = useState<{ enabled: boolean; frequency: string; contentTypes: string[]; postsPerRun: number }>({ enabled: false, frequency: "daily", contentTypes: ["Post"], postsPerRun: 3 });
  const [savingAutoGen, setSavingAutoGen] = useState(false);

  const [scheduleConfig, setScheduleConfig] = useState<{ schedule: string; autoPublish: boolean; autoApprovePeggyContent: boolean; autoPublishPlatforms: Record<string, boolean>; lastRun: string | null; nextRun: string | null }>({ schedule: "manual", autoPublish: false, autoApprovePeggyContent: false, autoPublishPlatforms: { linkedin: false, instagram: false, facebook: false, x: false }, lastRun: null, nextRun: null });
  const [savingSchedule, setSavingSchedule] = useState(false);

  type AutonomyLevel = "manual" | "smart" | "always_on";
  const [agentAutonomy, setAgentAutonomy] = useState<Record<string, { autonomyLevel: AutonomyLevel; confidenceThreshold: number }>>({
    draper: { autonomyLevel: "smart", confidenceThreshold: 80 },
    peggy: { autonomyLevel: "smart", confidenceThreshold: 80 },
    pete: { autonomyLevel: "manual", confidenceThreshold: 90 },
    rogers: { autonomyLevel: "always_on", confidenceThreshold: 70 },
  });
  const [savingAutonomy, setSavingAutonomy] = useState(false);

  const [wpUrl, setWpUrl] = useState("");
  const [wpUsername, setWpUsername] = useState("");
  const [wpAppPassword, setWpAppPassword] = useState("");
  const [wpSaving, setWpSaving] = useState(false);
  const [wpShowPassword, setWpShowPassword] = useState(false);
  const [blogConfig, setBlogConfig] = useState<{
    blogGenerationEnabled: boolean;
    authorName: string;
    primaryKeyword: string;
    secondaryKeywords: string;
    targetAudience: string;
    contentPillars: string;
    toneInstructions: string;
    minSeoScore: number;
    blogPublishWorkflow: "auto_publish" | "approval_required";
    rankMathTitle: string;
    rankMathDescription: string;
    rankMathFocusKeyword: string;
    categoryIds: string;
    tagIds: string;
  }>({
    blogGenerationEnabled: true,
    authorName: "",
    primaryKeyword: "",
    secondaryKeywords: "",
    targetAudience: "",
    contentPillars: "",
    toneInstructions: "",
    minSeoScore: 95,
    blogPublishWorkflow: "approval_required", // the server default too (peggy-tools saveBlogDraftAndRepurpose)
    rankMathTitle: "",
    rankMathDescription: "",
    rankMathFocusKeyword: "",
    categoryIds: "",
    tagIds: "",
  });
  const [cfZoneId, setCfZoneId] = useState("");
  const [cfApiToken, setCfApiToken] = useState("");
  const [cfSaving, setCfSaving] = useState(false);
  const [blogConfigSaving, setBlogConfigSaving] = useState(false);
  const [wpTaxonomy, setWpTaxonomy] = useState<{ categories: {id:number,name:string,slug:string,count:number}[], tags: {id:number,name:string,slug:string,count:number}[] }>({ categories: [], tags: [] });
  const [wpTaxonomyLoading, setWpTaxonomyLoading] = useState<{categories:boolean,tags:boolean}>({categories:false,tags:false});
  const [wpBrowseOpen, setWpBrowseOpen] = useState<{categories:boolean,tags:boolean}>({categories:false,tags:false});

  const [leadApiKey, setLeadApiKey] = useState<string | null>(null);
  const [metaWebhookInfo, setMetaWebhookInfo] = useState<{ webhookUrl: string; verifyToken: string } | null>(null);
  const [copiedWebhook, setCopiedWebhook] = useState<string | null>(null);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);
  const [showSnippetType, setShowSnippetType] = useState<"html" | "curl" | "zapier">("html");

  const [extractingBrand, setExtractingBrand] = useState(false);
  const [extractedBrand, setExtractedBrand] = useState<{ logoUrl: string | null; brandColors: string[]; tagline?: string; fontFamily?: string; brandPersonality?: string } | null>(null);
  const [stampLogoOnContent, setStampLogoOnContent] = useState(false);
  const [stampFields, setStampFields] = useState({ showLogo: true, showPhone: false, showEmail: false, showWebsite: false });
  const [savingStamp, setSavingStamp] = useState(false);

  const { data: brandKit } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"],
    enabled: !!activeWorkspaceId,
  });

  const { data: knowledgeDoc, isLoading: kdLoading, refetch: kdRefetch } = useQuery<any>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "knowledge"],
    queryFn: () => fetch(`/api/workspaces/${activeWorkspaceId}/knowledge`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
    enabled: !!activeWorkspaceId && activeSettingsTab === "agents",
    staleTime: 30000,
  });

  useEffect(() => {
    if (brandKit) {
      setStampLogoOnContent(!!brandKit.stampLogoOnContent);
      if (brandKit.stampFields) {
        setStampFields({
          showLogo: brandKit.stampFields.showLogo !== false,
          showPhone: !!brandKit.stampFields.showPhone,
          showEmail: !!brandKit.stampFields.showEmail,
          showWebsite: !!brandKit.stampFields.showWebsite,
        });
      }
    }
  }, [brandKit]);

  async function saveStampLogo(value: boolean) {
    if (!activeWorkspaceId) return;
    setSavingStamp(true);
    try {
      await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}/brand-kit`, {
        logoUrl: brandKit?.logoUrl || form.logoUrl || null,
        logoDarkUrl: brandKit?.logoDarkUrl || null,
        primaryColor: brandKit?.primaryColor || form.brandColors[0] || "#1a5276",
        secondaryColor: brandKit?.secondaryColor || form.brandColors[1] || "#2e86c1",
        accentColor: brandKit?.accentColor || form.brandColors[2] || "#f39c12",
        fontPreference: brandKit?.fontPreference || "modern",
        brandVoice: brandKit?.brandVoice || form.brandVoice || "professional but approachable",
        tagline: brandKit?.tagline || null,
        photoStyle: brandKit?.photoStyle || "real photography",
        stampLogoOnContent: value,
        stampFields,
      });
      setStampLogoOnContent(value);
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"] });
      toast({ title: value ? "Brand stamp enabled" : "Brand stamp disabled", description: value ? "Your logo and selected contact info will be baked into generated content images." : "Brand stamp disabled." });
    } catch {
      toast({ title: "Save failed", description: "Could not update brand stamp setting.", variant: "destructive" });
    } finally {
      setSavingStamp(false);
    }
  }

  async function saveStampFields(newFields: typeof stampFields) {
    if (!activeWorkspaceId) return;
    setSavingStamp(true);
    try {
      await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}/brand-kit`, {
        logoUrl: brandKit?.logoUrl || form.logoUrl || null,
        logoDarkUrl: brandKit?.logoDarkUrl || null,
        primaryColor: brandKit?.primaryColor || form.brandColors[0] || "#1a5276",
        secondaryColor: brandKit?.secondaryColor || form.brandColors[1] || "#2e86c1",
        accentColor: brandKit?.accentColor || form.brandColors[2] || "#f39c12",
        fontPreference: brandKit?.fontPreference || "modern",
        brandVoice: brandKit?.brandVoice || form.brandVoice || "professional but approachable",
        tagline: brandKit?.tagline || null,
        photoStyle: brandKit?.photoStyle || "real photography",
        stampLogoOnContent,
        stampFields: newFields,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"] });
      toast({ title: "Stamp fields saved" });
    } catch {
      toast({ title: "Save failed", description: "Could not update stamp fields.", variant: "destructive" });
    } finally {
      setSavingStamp(false);
    }
  }

  const [linkedinOrgId, setLinkedinOrgId] = useState<string>("");
  const [savingLinkedinOrg, setSavingLinkedinOrg] = useState(false);

  const { data: linkedinOrgsData } = useQuery<{ organizations: { orgId: string; orgUrn: string; name: string }[] }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "linkedin", "organizations"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/linkedin/organizations`, { credentials: "include" });
      if (!res.ok) return { organizations: [] };
      return res.json();
    },
    enabled: !!activeWorkspaceId && !!(workspace?.integrations as any)?.["linkedin"]?.connected,
  });

  const { data: scopeCheckData } = useQuery<{ linkedinNeedsReconnect: boolean; metaNeedsReconnect: boolean }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "oauth-scope-check"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/oauth-scope-check`, { credentials: "include" });
      if (!res.ok) return { linkedinNeedsReconnect: false, metaNeedsReconnect: false };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);

  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState("");
  const [resetting, setResetting] = useState(false);

  const [externalApiKey, setExternalApiKey] = useState<string | null>(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [keyPrefix, setKeyPrefix] = useState<string | null>(null);
  const [generatingExternalKey, setGeneratingExternalKey] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);

  const [nurtureGoal, setNurtureGoal] = useState<string>("appointment");
  const [savingNurtureGoal, setSavingNurtureGoal] = useState(false);
  const [kdEditMode, setKdEditMode] = useState(false);
  const [kdForm, setKdForm] = useState({ brandVoice: "", industry: "", targetAudience: "", currentGoals: "", strategySnapshot: "" });
  const [kdContentPrefs, setKdContentPrefs] = useState({ platforms: "", postLength: "", formats: "", topicsToAvoid: "", preferredPostingDays: "" });
  const [kdSaving, setKdSaving] = useState(false);
  const [kdNewPreference, setKdNewPreference] = useState("");
  const [kdAddingPref, setKdAddingPref] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ agentRunComplete: true, hotLeadAlert: true, agentWaitingForInput: true, campaignLaunchFailure: true, emailDigestFrequency: "instant" as "instant" | "daily" | "weekly" | "never" });
  const [savingNotifPrefs, setSavingNotifPrefs] = useState(false);
  const [telegramBotToken, setTelegramBotToken] = useState("");
  type TelegramConfigState = {
    status: "connected" | "waiting_for_start" | "not_configured";
    botUsername?: string;
    linkedAt?: string;
    enabled?: boolean;
    alertsConfig?: Record<string, boolean>;
    linkCode?: string | null;
    quietHoursStart?: number;
    quietHoursEnd?: number;
    timezone?: string;
  } | null;
  const [telegramConfig, setTelegramConfig] = useState<TelegramConfigState>(null);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramSaving, setTelegramSaving] = useState(false);
  const [telegramAlerts, setTelegramAlerts] = useState({
    morningBrief: true, hotLeads: true, contentApprovals: true,
    publishFailures: true, campaignAlerts: true, harryIntel: false,
    strategyGaps: true, draperQuestions: true,
  });
  const [telegramQuietStart, setTelegramQuietStart] = useState(22);
  const [telegramQuietEnd, setTelegramQuietEnd] = useState(7);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [loadingAuditLogs, setLoadingAuditLogs] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [apiRefOpen, setApiRefOpen] = useState(false);
  const [activeSetupTab, setActiveSetupTab] = useState<"claude" | "n8n" | "rest">("claude");

  const { data: apiUsageStats } = useQuery<{ total7d: number; total24h: number; lastUsedAt: string | null; topActions: { action: string; count: number }[] }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "api-usage"],
    queryFn: () => apiRequest("GET", `/api/workspaces/${activeWorkspaceId}/api-usage`).then(r => r.json()),
    enabled: !!activeWorkspaceId && (hasApiKey || !!externalApiKey),
    staleTime: 60_000,
  });
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [newWebhookUrl, setNewWebhookUrl] = useState("");
  const [newWebhookEvents, setNewWebhookEvents] = useState("*");
  const [addingWebhook, setAddingWebhook] = useState(false);

  useEffect(() => {
    if (activeWorkspaceId) {
      fetch(`/api/workspaces/${activeWorkspaceId}/schedule-config`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) {
          const platforms = data.autoPublishPlatforms || {};
          setScheduleConfig({
            schedule: data.agentSchedule || data.schedule || "manual",
            autoPublish: data.autoPublish ?? false,
            autoApprovePeggyContent: data.autoApprovePeggyContent ?? false,
            autoPublishPlatforms: {
              linkedin: platforms.linkedin ?? false,
              instagram: platforms.instagram ?? false,
              facebook: platforms.facebook ?? false,
              x: platforms.x ?? false,
            },
            lastRun: data.lastAgentRun || data.lastRun || null,
            nextRun: data.nextAgentRun || data.nextRun || null,
          });
          if (data.autoGeneration) {
            setAutoGenConfig(data.autoGeneration);
          }
        }})
        .catch(() => {});

      fetch(`/api/workspaces/${activeWorkspaceId}/api-key`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setLeadApiKey(data.apiKey); })
        .catch(() => {});

      fetch(`/api/meta-webhook-info?workspaceId=${activeWorkspaceId}`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data) setMetaWebhookInfo(data); })
        .catch(() => {});

      fetch(`/api/workspaces/${activeWorkspaceId}/external-api-key`, { credentials: "include" })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) {
            setExternalApiKey(data.apiKey || null);
            setHasApiKey(data.hasKey || !!data.apiKey);
            setKeyPrefix(data.prefix || (data.apiKey ? data.apiKey.substring(0, 12) : null));
          }
        })
        .catch(() => {});

      fetch(`/api/workspaces/${activeWorkspaceId}/webhooks`, { credentials: "include" })
        .then(r => r.ok ? r.json() : [])
        .then(data => setWebhooks(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
  }, [activeWorkspaceId]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get("connected");
    const oauthError = params.get("oauth_error");
    if (connected) {
      toast({ title: `${connected.charAt(0).toUpperCase() + connected.slice(1)} connected successfully` });
      window.history.replaceState({}, '', '/settings');
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
    }
    if (oauthError) {
      toast({ title: "Connection failed", description: oauthError, variant: "destructive" });
      window.history.replaceState({}, '', '/settings');
    }
  }, []);

  const [initializedFor, setInitializedFor] = useState<number | null>(null);
  useEffect(() => {
    if (workspace && workspace.id !== initializedFor) {
      const socials = (workspace.socialUrls || {}) as Record<string, string>;
      const contact = (workspace.contactInfo || {}) as Record<string, string>;
      setForm({
        name: workspace.name || "",
        brandName: (workspace as any).brandName || (workspace as any).name || "",
        url: workspace.url || "",
        industry: workspace.industry || "",
        targetAudience: workspace.targetAudience || "",
        brandVoice: workspace.brandVoice || "",
        brandTone: workspace.brandTone || "",
        logoUrl: workspace.logoUrl || "",
        brandColors: workspace.brandColors || [],
        contactInfo: {
          phone: contact.phone || "",
          email: contact.email || "",
          address: contact.address || "",
          website: contact.website || "",
          city: contact.city || "",
        } as any,
        socialUrls: {
          linkedin: socials.linkedin || "",
          x: socials.x || "",
          instagram: socials.instagram || "",
          facebook: socials.facebook || "",
          tiktok: socials.tiktok || "",
          website: socials.website || "",
        },
      });
      const ac = (workspace as any).agentConfig;
      if (ac) {
        setAgentAutonomy({
          draper: ac.draper || { autonomyLevel: "smart", confidenceThreshold: 80 },
          peggy: ac.peggy || { autonomyLevel: "smart", confidenceThreshold: 80 },
          pete: ac.pete || { autonomyLevel: "manual", confidenceThreshold: 90 },
          rogers: ac.rogers || { autonomyLevel: "always_on", confidenceThreshold: 70 },
        });
      }
      if ((workspace as any).defaultNurtureGoal) setNurtureGoal((workspace as any).defaultNurtureGoal);
      const np = (workspace as any).notificationPreferences;
      if (np) setNotifPrefs({ agentRunComplete: np.agentRunComplete ?? true, hotLeadAlert: np.hotLeadAlert ?? true, agentWaitingForInput: np.agentWaitingForInput ?? true, campaignLaunchFailure: np.campaignLaunchFailure ?? true, emailDigestFrequency: np.emailDigestFrequency || "instant" });
      // WordPress credentials — URL is visible, password is never returned by API
      setWpUrl((workspace as any).wordpressUrl || "");
      setWpUsername((workspace as any).wordpressUsername || "");
      setWpAppPassword(""); // Password is write-only; cleared on load
      // Blog guardrails config — always reset to avoid stale data when switching workspaces
      const bc = (workspace as any).blogConfig || {};
      setBlogConfig({
        blogGenerationEnabled: bc.blogGenerationEnabled !== false,
        authorName: bc.authorName || "",
        primaryKeyword: bc.primaryKeyword || "",
        secondaryKeywords: bc.secondaryKeywords || "",
        targetAudience: bc.targetAudience || "",
        contentPillars: bc.contentPillars || "",
        toneInstructions: bc.toneInstructions || "",
        minSeoScore: typeof bc.minSeoScore === "number" ? bc.minSeoScore : 95,
        blogPublishWorkflow: bc.blogPublishWorkflow || "approval_required",
        rankMathTitle: bc.rankMathTitle || "",
        rankMathDescription: bc.rankMathDescription || "",
        rankMathFocusKeyword: bc.rankMathFocusKeyword || "",
        categoryIds: Array.isArray(bc.categoryIds) ? bc.categoryIds.join(", ") : (bc.categoryIds || ""),
        tagIds: Array.isArray(bc.tagIds) ? bc.tagIds.join(", ") : (bc.tagIds || ""),
      });
      setCfZoneId((workspace as any).cloudflareZoneId || "");
      setCfApiToken("");
      setInitializedFor(workspace.id);
    }
  }, [workspace, initializedFor]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    setTelegramLoading(true);
    fetch(`/api/telegram/config/${activeWorkspaceId}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        if (data && data.status !== "not_configured") {
          setTelegramConfig(data);
          if (data.alertsConfig) setTelegramAlerts(data.alertsConfig);
          setTelegramQuietStart(typeof data.quietHoursStart === "number" ? data.quietHoursStart : 22);
          setTelegramQuietEnd(typeof data.quietHoursEnd === "number" ? data.quietHoursEnd : 7);
        } else {
          setTelegramConfig(null);
          setTelegramQuietStart(22);
          setTelegramQuietEnd(7);
        }
      })
      .catch(() => {})
      .finally(() => setTelegramLoading(false));
  }, [activeWorkspaceId]);

  const updateMutation = useMutation({
    mutationFn: async (data: typeof form) => {
      const res = await apiRequest("PUT", `/api/workspaces/${activeWorkspaceId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      toast({ title: "Settings saved", description: "Workspace configuration updated." });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to save settings.", variant: "destructive" });
    },
  });

  if (!activeWorkspaceId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="no-workspace-state">
        <Settings size={48} className="text-muted-foreground/50 mb-4" />
        <h2 className="text-xl font-display font-bold text-muted-foreground">No Workspace Selected</h2>
        <p className="text-muted-foreground/70 mt-2 text-sm">Select a workspace to manage settings.</p>
      </div>
    );
  }

  const socialFields = [
    { key: "linkedin", label: "LinkedIn", icon: SiLinkedin, placeholder: "https://linkedin.com/company/..." },
    { key: "x", label: "X (Twitter)", icon: SiX, placeholder: "https://x.com/..." },
    { key: "instagram", label: "Instagram", icon: SiInstagram, placeholder: "https://instagram.com/..." },
    { key: "facebook", label: "Facebook", icon: SiFacebook, placeholder: "https://facebook.com/..." },
    { key: "tiktok", label: "TikTok", icon: SiTiktok, placeholder: "https://tiktok.com/@..." },
  ];

  function addColor() {
    if (newColor && !form.brandColors.includes(newColor)) {
      setForm({ ...form, brandColors: [...form.brandColors, newColor] });
    }
  }

  function removeColor(color: string) {
    setForm({ ...form, brandColors: form.brandColors.filter(c => c !== color) });
  }

  async function handleExtractBrand() {
    if (!activeWorkspaceId) return;
    setExtractingBrand(true);
    setExtractedBrand(null);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/extract-brand`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        toast({ title: "Extraction failed", description: err.message || "Could not extract brand assets from website.", variant: "destructive" });
        return;
      }
      const data = await res.json();
      if (!data.logoUrl && (!data.brandColors || data.brandColors.length === 0)) {
        toast({ title: "Nothing found", description: "No logo or brand colours could be extracted from the website. Try adding an og:image or theme-color meta tag.", variant: "destructive" });
        return;
      }
      setExtractedBrand(data);
      // Invalidate brand kit so saved tagline/font refresh automatically
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "brand-kit"] });
      const extras = [data.tagline && `Tagline: "${data.tagline}"`, data.fontFamily && `Font: ${data.fontFamily}`].filter(Boolean).join(" · ");
      toast({ title: "Brand extracted", description: extras ? `Logo & colours found. ${extras}` : "Logo and colours found. Click Apply to use them." });
    } catch {
      toast({ title: "Error", description: "Failed to contact the server.", variant: "destructive" });
    } finally {
      setExtractingBrand(false);
    }
  }

  function applyExtractedBrand() {
    if (!extractedBrand) return;
    const updates: any = {};
    if (extractedBrand.logoUrl) updates.logoUrl = extractedBrand.logoUrl;
    if (extractedBrand.brandColors.length > 0) {
      const merged = [...form.brandColors];
      for (const c of extractedBrand.brandColors) {
        if (!merged.includes(c)) merged.push(c);
      }
      updates.brandColors = merged;
    }
    setForm({ ...form, ...updates });
    setExtractedBrand(null);
    toast({ title: "Applied", description: "Brand assets applied. Save your settings to persist them." });
  }

  async function saveWordPressCredentials(disconnect?: boolean) {
    if (!activeWorkspaceId) return;
    setWpSaving(true);
    try {
      const body = disconnect
        ? { disconnect: true }
        : { wordpressUrl: wpUrl.trim(), wordpressUsername: wpUsername.trim(), wordpressAppPassword: wpAppPassword || undefined };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/wordpress`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        if (disconnect) {
          setWpUrl(""); setWpUsername(""); setWpAppPassword("");
          toast({ title: "WordPress disconnected" });
        } else {
          setWpAppPassword(""); // Clear after save — write-only
          toast({ title: "WordPress connected", description: `Connected to ${data.wordpressUrl}` });
        }
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      } else {
        toast({ title: "Failed to save", description: data.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Failed to save WordPress credentials", variant: "destructive" });
    } finally {
      setWpSaving(false);
    }
  }

  async function saveBlogConfig() {
    if (!activeWorkspaceId) return;
    setBlogConfigSaving(true);
    try {
      // Parse categoryIds and tagIds from comma-separated strings to integer arrays
      const parseIds = (raw: string): number[] =>
        raw.split(",").map(s => parseInt(s.trim())).filter(n => Number.isInteger(n) && n > 0);
      const payload = {
        ...blogConfig,
        categoryIds: blogConfig.categoryIds ? parseIds(blogConfig.categoryIds) : [],
        tagIds: blogConfig.tagIds ? parseIds(blogConfig.tagIds) : [],
      };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ blogConfig: payload }),
      });
      if (res.ok) {
        toast({ title: "Blog guardrails saved" });
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      } else {
        const data = await res.json();
        toast({ title: "Failed to save blog guardrails", description: data.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Failed to save blog guardrails", variant: "destructive" });
    } finally {
      setBlogConfigSaving(false);
    }
  }

  async function browseTaxonomy(type: 'categories'|'tags') {
    setWpTaxonomyLoading(p => ({...p,[type]:true}));
    setWpBrowseOpen(p => ({...p,[type]:true}));
    try {
      const r = await fetch(`/api/workspaces/${activeWorkspaceId}/wordpress/${type}`, { credentials: 'include' });
      if (r.ok) { const data = await r.json(); setWpTaxonomy(p => ({...p,[type]:data})); }
      else { toast({ title: 'Could not load WordPress ' + type, variant: 'destructive' }); setWpBrowseOpen(p=>({...p,[type]:false})); }
    } catch { toast({ title: 'Could not reach WordPress', variant: 'destructive' }); setWpBrowseOpen(p=>({...p,[type]:false})); }
    finally { setWpTaxonomyLoading(p => ({...p,[type]:false})); }
  }

  function toggleTaxonomyId(field: 'categoryIds'|'tagIds', id: number) {
    setBlogConfig(v => {
      const ids = v[field].split(',').map((s:string)=>s.trim()).filter(Boolean);
      const idx = ids.indexOf(String(id));
      const next = idx >= 0 ? ids.filter((x:string) => x !== String(id)) : [...ids, String(id)];
      return {...v, [field]: next.join(', ')};
    });
  }

  async function saveCloudflareSettings(disconnect?: boolean) {
    if (!activeWorkspaceId) return;
    setCfSaving(true);
    try {
      const body = disconnect
        ? { disconnect: true }
        : { cloudflareZoneId: cfZoneId.trim(), cloudflareApiToken: cfApiToken.trim() || undefined };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/cloudflare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        if (disconnect) { setCfZoneId(""); setCfApiToken(""); toast({ title: "Cloudflare disconnected" }); }
        else { setCfApiToken(""); toast({ title: "Cloudflare cache settings saved" }); }
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      } else {
        toast({ title: "Failed to save Cloudflare settings", description: data.message, variant: "destructive" });
      }
    } catch {
      toast({ title: "Failed to save Cloudflare settings", variant: "destructive" });
    } finally {
      setCfSaving(false);
    }
  }

  async function handleDeleteWorkspace() {
    if (!activeWorkspaceId || !workspace) return;
    if (deleteConfirmName.trim().toLowerCase() !== workspace.name.trim().toLowerCase()) {
      toast({ title: "Name doesn't match", description: "Type the workspace name exactly to confirm deletion.", variant: "destructive" });
      return;
    }
    setDeleting(true);
    const deletedId = activeWorkspaceId;
    const deletedName = workspace.name;
    try {
      const res = await fetch(`/api/workspaces/${deletedId}`, {
        method: "DELETE",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ confirmName: deleteConfirmName.trim() }),
      });
      if (res.status === 404) {
        // Already deleted — treat as success and clean up
        toast({ title: "Already deleted", description: `"${deletedName}" was already removed.` });
      } else if (!res.ok) {
        throw new Error("Failed to delete workspace");
      } else {
        toast({ title: "Workspace deleted", description: `"${deletedName}" has been permanently deleted.` });
      }
      // Clear ALL cached data for the deleted workspace so nothing keeps polling it
      queryClient.removeQueries({ queryKey: ["/api/workspaces", deletedId] });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      // Switch to a remaining workspace (or null) and navigate away from settings
      const remaining = workspaces.filter((w) => w.id !== deletedId);
      setActiveWorkspaceId(remaining.length > 0 ? remaining[0].id : null);
      setShowDeleteConfirm(false);
      setDeleteConfirmName("");
      setLocation("/");
    } catch {
      toast({ title: "Error", description: "Failed to delete workspace.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  }

  async function handleResetWorkspace() {
    if (!activeWorkspaceId) return;
    if (resetConfirmText.trim() !== "RESET") {
      toast({ title: "Invalid confirmation", description: "Type RESET to confirm.", variant: "destructive" });
      return;
    }
    setResetting(true);
    try {
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/reset`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Reset failed");
      toast({ title: "Workspace Reset", description: "All data has been cleared. Refreshing..." });
      setTimeout(() => window.location.reload(), 1500);
    } catch {
      toast({ title: "Error", description: "Failed to reset workspace.", variant: "destructive" });
    } finally {
      setResetting(false);
    }
  }

  async function testApiConnection() {
    if (!externalApiKey) return;
    setTestingConnection(true);
    try {
      const res = await fetch("/api/v1/workspace", {
        headers: { "X-API-Key": externalApiKey }
      });
      if (res.ok) {
        const data = await res.json();
        toast({ title: "Connection Successful!", description: `Connected to workspace: ${data.data.name}` });
      } else {
        toast({ title: "Connection Failed", description: "Invalid API key or server error.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Connection Error", variant: "destructive" });
    } finally {
      setTestingConnection(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-8" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-display font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground mt-1 text-sm">Configure your workspace profile, brand identity, and social presence.</p>
      </div>

      {isViewer && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20" data-testid="viewer-settings-notice">
          <AlertTriangle size={14} className="text-amber-400 shrink-0" />
          <p className="text-sm text-amber-400/90">You have <strong>viewer access</strong> to this workspace. You need Editor or Owner access to make changes.</p>
        </div>
      )}

      <Tabs
        value={activeSettingsTab}
        onValueChange={(v) => { if (isSettingsTab(v)) setLocation(`/settings?tab=${v}`); }}
        data-testid="settings-tabs-nav"
      >
        <TabsList className="w-full justify-start h-auto p-0 bg-transparent border-b border-border rounded-none overflow-x-auto">
          <TabsTrigger value="brand" data-testid="tab-settings-brand" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-400 px-4 py-2.5 text-sm">Brand & Business</TabsTrigger>
          <TabsTrigger value="publishing" data-testid="tab-settings-publishing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-400 px-4 py-2.5 text-sm">Publishing</TabsTrigger>
          <TabsTrigger value="agents" data-testid="tab-settings-agents" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-400 px-4 py-2.5 text-sm">Agents</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-settings-notifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-400 px-4 py-2.5 text-sm">Notifications & API</TabsTrigger>
          <TabsTrigger value="memory" data-testid="tab-settings-memory" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-400 px-4 py-2.5 text-sm">Draper Memory</TabsTrigger>
          <TabsTrigger value="routines" data-testid="tab-settings-routines" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-blue-400 px-4 py-2.5 text-sm">Routines</TabsTrigger>
          <TabsTrigger value="content-targeting" data-testid="tab-settings-content-targeting" className="rounded-none border-b-2 border-transparent data-[state=active]:border-teal-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-teal-400 px-4 py-2.5 text-sm whitespace-nowrap">Content Targeting</TabsTrigger>
          <TabsTrigger value="personal-brand" data-testid="tab-settings-personal-brand" className="rounded-none border-b-2 border-transparent data-[state=active]:border-purple-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-purple-400 px-4 py-2.5 text-sm whitespace-nowrap">Personal Brand</TabsTrigger>
          <TabsTrigger value="site-agent" data-testid="tab-settings-site-agent" className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-emerald-400 px-4 py-2.5 text-sm whitespace-nowrap">Site Agent</TabsTrigger>
          <TabsTrigger value="agency-branding" data-testid="tab-settings-agency-branding" className="rounded-none border-b-2 border-transparent data-[state=active]:border-violet-500 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-violet-400 px-4 py-2.5 text-sm whitespace-nowrap">Agency Branding</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="space-y-6 mt-6">
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Building2 size={20} className="text-blue-400" />
            <h2 className="font-display font-bold text-foreground">Business Info</h2>
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Workspace Name</label>
            <input
              data-testid="input-workspace-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
            />
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand / Business Name</label>
            <input
              data-testid="input-brand-name"
              value={form.brandName}
              onChange={(e) => setForm({ ...form, brandName: e.target.value })}
              placeholder="Your brand name (used in all AI-generated content)"
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
            />
            <p className="text-[10px] text-muted-foreground/50 mt-1">This is the name AI agents use when creating content, ads, and communications for your brand.</p>
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Website URL</label>
            <div className="flex items-center gap-2">
              <Globe size={16} className="text-muted-foreground/50" />
              <input
                data-testid="input-website-url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://example.com"
                className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Industry</label>
            <input
              data-testid="input-industry"
              value={form.industry}
              onChange={(e) => setForm({ ...form, industry: e.target.value })}
              placeholder="e.g., SaaS, E-commerce, Fintech"
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
            />
          </div>
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Image size={20} className="text-blue-400" />
            <h2 className="font-display font-bold text-foreground">Brand Kit</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Your brand assets are used when generating images, ads, and content. Logo placement and brand colors will appear in generated visuals.</p>

          <BrandKitPuller
            key={activeWorkspaceId ?? "none"}
            workspaceId={activeWorkspaceId}
            websiteUrl={workspace?.url || ""}
            onApply={(data) => {
              const updates: any = { ...form };
              if (data.logoUrl) updates.logoUrl = data.logoUrl;
              if (data.brandColors && data.brandColors.length > 0) {
                const merged = [...form.brandColors];
                for (const c of data.brandColors) {
                  if (!merged.includes(c)) merged.push(c);
                }
                updates.brandColors = merged;
              }
              setForm(updates);
            }}
          />

          {form.logoUrl && !isBareDomainUrl(form.logoUrl) && (
            <div className="flex items-center gap-3 mt-2">
              <div className="w-12 h-12 rounded-lg border border-border bg-muted/50 flex items-center justify-center overflow-hidden shrink-0">
                <img src={form.logoUrl} alt="Logo preview" className="max-w-full max-h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              </div>
              <span className="text-xs text-muted-foreground">Current logo</span>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4 py-3 px-4 bg-muted/30 border border-border rounded-xl">
              <div>
                <p className="text-sm font-medium text-foreground">Brand stamp on content</p>
                <p className="text-xs text-muted-foreground/70 mt-0.5">Bake your logo and contact info onto generated content images — what you see is what gets published.</p>
              </div>
              <button
                data-testid="toggle-stamp-logo"
                onClick={() => saveStampLogo(!stampLogoOnContent)}
                disabled={savingStamp || isViewer}
                title={isViewer ? "You need Editor or Owner access." : undefined}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-50 disabled:pointer-events-none ${stampLogoOnContent ? "bg-blue-600" : "bg-muted"}`}
                role="switch"
                aria-checked={stampLogoOnContent}
              >
                {savingStamp ? (
                  <span className="absolute inset-0 flex items-center justify-center">
                    <Loader2 size={12} className="animate-spin text-white" />
                  </span>
                ) : (
                  <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ${stampLogoOnContent ? "translate-x-5" : "translate-x-0"}`} />
                )}
              </button>
            </div>

            {stampLogoOnContent && (
              <div className="pl-2 space-y-3">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">What to show on stamp</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "showLogo", label: "Logo", desc: "Your brand logo" },
                    { key: "showPhone", label: "Phone", desc: form.contactInfo.phone || "No phone set" },
                    { key: "showEmail", label: "Email", desc: form.contactInfo.email || "No email set" },
                    { key: "showWebsite", label: "Website", desc: form.contactInfo.website || form.url || "No website set" },
                  ].map(({ key, label, desc }) => {
                    const isOn = stampFields[key as keyof typeof stampFields];
                    return (
                      <button
                        key={key}
                        data-testid={`toggle-stamp-field-${key}`}
                        onClick={() => {
                          const newFields = { ...stampFields, [key]: !isOn };
                          setStampFields(newFields);
                          saveStampFields(newFields);
                        }}
                        disabled={isViewer}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${isOn ? "border-blue-500/40 bg-blue-500/10 text-blue-300" : "border-border text-muted-foreground bg-muted/30 hover:border-border"}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isOn ? "bg-blue-600 border-blue-500" : "border-border"}`}>
                          {isOn && <Check size={10} className="text-white" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold">{label}</p>
                          <p className="text-[10px] text-muted-foreground/60 truncate">{desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-3">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">Preview</p>
                  <div className="relative w-full max-w-xs h-28 bg-gradient-to-br from-slate-700 to-slate-900 rounded-xl overflow-hidden border border-border">
                    <span className="absolute top-2 left-2 text-[10px] text-white/50">Sample image</span>
                    <div
                      className="absolute bottom-2 right-2 rounded-lg flex items-center gap-2 px-2.5 py-1.5"
                      style={{ backgroundColor: `${brandKit?.primaryColor || "#1a5276"}d9`, maxWidth: "80%" }}
                    >
                      {stampFields.showLogo && (brandKit?.logoUrl || form.logoUrl) && (
                        <img
                          src={brandKit?.logoUrl || form.logoUrl}
                          alt="Logo"
                          className="w-7 h-7 object-contain rounded shrink-0"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                      )}
                      <div className="min-w-0 space-y-0.5">
                        {stampFields.showPhone && form.contactInfo.phone && <p className="text-[9px] text-white truncate">{form.contactInfo.phone}</p>}
                        {stampFields.showEmail && form.contactInfo.email && <p className="text-[9px] text-white truncate">{form.contactInfo.email}</p>}
                        {stampFields.showWebsite && (form.contactInfo.website || form.url) && <p className="text-[9px] text-white truncate">{form.contactInfo.website || form.url}</p>}
                        {!stampFields.showPhone && !stampFields.showEmail && !stampFields.showWebsite && !stampFields.showLogo && (
                          <p className="text-[9px] text-white/50 italic">Select fields above</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand Colors</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {form.brandColors.map((color) => (
                <div key={color} className="flex items-center gap-1.5 bg-muted/50 border border-border rounded-lg px-2 py-1.5">
                  <div className="w-5 h-5 rounded-md border border-border" style={{ backgroundColor: color }} />
                  <span className="text-xs text-muted-foreground font-mono">{color}</span>
                  <button onClick={() => removeColor(color)} className="text-muted-foreground/50 hover:text-muted-foreground transition-colors" data-testid={`button-remove-color-${color}`}>
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer bg-transparent"
                data-testid="input-color-picker"
              />
              <input
                value={newColor}
                onChange={(e) => setNewColor(e.target.value)}
                placeholder="#3B82F6"
                className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground font-mono"
                data-testid="input-color-hex"
              />
              <button
                onClick={addColor}
                className="bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-600/30 transition-colors flex items-center gap-1.5"
                data-testid="button-add-color"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Phone size={20} className="text-blue-400" />
            <h2 className="font-display font-bold text-foreground">Contact Information</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Contact details can be included in generated ads, lead magnets, and image overlays.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Phone</label>
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-muted-foreground/50 shrink-0" />
                <input
                  data-testid="input-contact-phone"
                  value={form.contactInfo.phone}
                  onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, phone: e.target.value } })}
                  placeholder="(555) 123-4567"
                  className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Email</label>
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-muted-foreground/50 shrink-0" />
                <input
                  data-testid="input-contact-email"
                  value={form.contactInfo.email}
                  onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, email: e.target.value } })}
                  placeholder="info@example.com"
                  className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
                />
              </div>
            </div>
          </div>

          <div className="border-l-2 border-blue-500/60 pl-4 space-y-3">
            <div className="flex items-center gap-2">
              <MapPin size={14} className="text-blue-400 shrink-0" />
              <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Business Location</span>
            </div>
            <p className="text-xs text-blue-300/70">This city is used to find local competitors and target your ads correctly. Required for accurate strategy — leave blank and Draper may guess incorrectly.</p>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">City &amp; Province / State</label>
              <input
                data-testid="input-contact-city"
                value={(form.contactInfo as any).city || ""}
                onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, city: e.target.value } as any })}
                placeholder="e.g., Mississauga, ON"
                className="w-full bg-muted/50 border border-blue-500/40 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Full Street Address <span className="text-muted-foreground/50 font-normal normal-case tracking-normal">(optional)</span></label>
              <input
                data-testid="input-contact-address"
                value={form.contactInfo.address}
                onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, address: e.target.value } })}
                placeholder="123 Main St, City, Province"
                className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Website (for contact)</label>
            <div className="flex items-center gap-2">
              <Globe size={14} className="text-muted-foreground/50 shrink-0" />
              <input
                data-testid="input-contact-website"
                value={form.contactInfo.website}
                onChange={(e) => setForm({ ...form, contactInfo: { ...form.contactInfo, website: e.target.value } })}
                placeholder="https://example.com"
                className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
          </div>
        </div>

        </TabsContent>

        <TabsContent value="publishing" className="space-y-6 mt-6">
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" id="channel-connections">
          <div className="flex items-center gap-3 mb-2">
            <Link2 size={20} className="text-blue-400" />
            <h2 className="font-display font-bold text-foreground">Channel Connections</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Connect your social media accounts to enable direct publishing by Peggy (content) and Pete (ads). OAuth tokens are securely stored per workspace.</p>

          <div className="space-y-3">
            {[
              { key: "linkedin" as const, label: "LinkedIn", icon: SiLinkedin, color: "text-[#0A66C2]", bgColor: "bg-[#0A66C2]/10", borderColor: "border-[#0A66C2]/30", desc: "Company pages, posts & articles" },
              { key: "meta" as const, label: "Meta (Facebook & Instagram)", icon: SiFacebook, color: "text-[#1877F2]", bgColor: "bg-[#1877F2]/10", borderColor: "border-[#1877F2]/30", desc: "Pages, stories, reels & ads" },
              { key: "instagram_business" as const, label: "Instagram Business", icon: SiInstagram, color: "text-[#E1306C]", bgColor: "bg-[#E1306C]/10", borderColor: "border-[#E1306C]/30", desc: "Business & Creator accounts — no Facebook required" },
              { key: "google" as const, label: "Google (Ads & Business)", icon: SiGoogle, color: "text-[#4285F4]", bgColor: "bg-[#4285F4]/10", borderColor: "border-[#4285F4]/30", desc: "Google Ads, Business Profile" },
              { key: "x" as const, label: "X (Twitter)", icon: SiX, color: "text-foreground", bgColor: "bg-muted", borderColor: "border-border", desc: "Posts, threads & promotions" },
            ].map(({ key, label, icon: Icon, color, bgColor, borderColor, desc }) => {
              const integrations = workspace?.integrations as any;
              const isConnected = integrations?.[key]?.connected || false;
              const accountId = integrations?.[key]?.accountId || "";
              const fbConnected = key === "meta" && integrations?.["facebook"]?.connected;
              const igConnected = key === "meta" && integrations?.["instagram"]?.connected;

              return (
                <div key={key} className={`flex items-center justify-between gap-3 p-3 md:p-4 rounded-xl border transition-all flex-wrap ${isConnected ? `${bgColor} ${borderColor}` : "bg-muted/30 border-border"}`} data-testid={`channel-${key}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${isConnected ? bgColor : "bg-muted/50"} flex items-center justify-center`}>
                      <Icon size={18} className={isConnected ? color : "text-muted-foreground/50"} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isConnected ? "text-foreground" : "text-muted-foreground"}`}>{label}</p>
                      <p className="text-[10px] text-muted-foreground/60">{desc}</p>
                      {isConnected && accountId && (
                        <p className="text-[10px] text-green-400/60 mt-0.5 flex items-center gap-1">
                          <CheckCircle2 size={8} />
                          Connected: {accountId}
                        </p>
                      )}
                      {key === "meta" && isConnected && (
                        <div className="mt-1 space-y-0.5">
                          <div className="flex items-center gap-3">
                            <p className={`text-[10px] flex items-center gap-1 ${fbConnected ? "text-blue-400/70" : "text-muted-foreground/50"}`}>
                              <SiFacebook size={8} /> {fbConnected ? `FB Page: ${integrations?.["facebook"]?.pageName || "Connected"}` : "No FB page found"}
                            </p>
                            <p className={`text-[10px] flex items-center gap-1 ${igConnected ? "text-pink-400/70" : "text-muted-foreground/50"}`} data-testid="meta-ig-status">
                              <SiInstagram size={8} />
                              {igConnected
                                ? integrations?.["instagram"]?.igUsername
                                  ? `@${integrations["instagram"].igUsername}`
                                  : `IG linked via ${integrations?.["instagram"]?.viaPage || "Facebook Page"}`
                                : "No IG Business account linked to this Page"}
                            </p>
                          </div>
                          <p className="text-[10px] text-muted-foreground/50 leading-tight">
                            Instagram posts go to the IG Business account linked to your Facebook Page in Meta Business Suite — no separate IG login needed.
                          </p>
                          <p className="text-[10px] text-amber-500/50 leading-tight" data-testid="meta-instagram-live-mode-note">
                            Note: Instagram posting requires your Meta app to be in Live mode, or your account added as an app tester in Meta for Developers.
                          </p>
                        </div>
                      )}
                      {key === "instagram_business" && (
                        <p className="text-[10px] text-amber-500/80 mt-0.5" data-testid="instagram-business-requirement">
                          Requires a Business or Creator account.{" "}
                          <a
                            href="https://help.instagram.com/502981923235522"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline underline-offset-2 hover:text-amber-400"
                            data-testid="link-instagram-convert"
                          >
                            How to switch
                          </a>
                        </p>
                      )}
                      {key === "instagram_business" && isConnected && (() => {
                        const igInteg = integrations?.["instagram_business"];
                        const tokenExpiresAt = igInteg?.tokenExpiresAt ? new Date(igInteg.tokenExpiresAt) : null;
                        const now = Date.now();
                        const msLeft = tokenExpiresAt ? tokenExpiresAt.getTime() - now : null;
                        const daysLeft = msLeft !== null ? Math.ceil(msLeft / 86400000) : null;
                        const isExpired = msLeft !== null && msLeft <= 0;
                        const isExpiringSoon = !isExpired && daysLeft !== null && daysLeft <= 14;
                        return (
                          <>
                            <div className="mt-0.5 flex items-center gap-2 flex-wrap" data-testid="instagram-business-info">
                              <p className="text-[10px] text-pink-400/70 flex items-center gap-1" data-testid="instagram-business-username">
                                <SiInstagram size={8} />
                                {igInteg?.username ? `@${igInteg.username}` : "Connected"}
                              </p>
                              {igInteg?.accountType && (
                                <span className="text-[10px] text-pink-400/50 bg-pink-400/10 rounded px-1 py-0.5" data-testid="instagram-business-account-type">
                                  {String(igInteg.accountType).charAt(0) + String(igInteg.accountType).slice(1).toLowerCase()}
                                </span>
                              )}
                              {tokenExpiresAt && !isExpired && !isExpiringSoon && (
                                <span className="text-[10px] text-green-400/60 bg-green-400/10 rounded px-1 py-0.5 flex items-center gap-0.5" data-testid="instagram-token-status-active">
                                  <CheckCircle2 size={7} /> Active · expires {tokenExpiresAt.toLocaleDateString()}
                                </span>
                              )}
                              {isExpired && (
                                <span className="text-[10px] text-red-400/80 bg-red-400/10 rounded px-1 py-0.5" data-testid="instagram-token-status-expired">
                                  ⚠ Token expired
                                </span>
                              )}
                              {isExpiringSoon && (
                                <span className="text-[10px] text-amber-400/80 bg-amber-400/10 rounded px-1 py-0.5" data-testid="instagram-token-status-expiring">
                                  ⚠ Expires in {daysLeft}d
                                </span>
                              )}
                            </div>
                            {isExpired && (
                              <div className="mt-1 flex items-center gap-2 p-1.5 rounded-lg bg-red-500/10 border border-red-500/20" data-testid="instagram-token-expired-banner">
                                <span className="text-[10px] text-red-400 flex-1">
                                  Instagram token has expired. Publishing is paused. Reconnect to resume.
                                </span>
                                <button
                                  onClick={() => { window.location.href = `/api/oauth/instagram_business/authorize?wsId=${activeWorkspaceId}`; }}
                                  className="text-[10px] font-bold text-red-400 border border-red-400/40 rounded px-2 py-0.5 hover:bg-red-400/10 transition-colors whitespace-nowrap"
                                  data-testid="button-reconnect-instagram-expired"
                                >
                                  Reconnect now
                                </button>
                              </div>
                            )}
                            {isExpiringSoon && !isExpired && (
                              <div className="mt-1 flex items-center gap-2 p-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20" data-testid="instagram-token-expiring-banner">
                                <span className="text-[10px] text-amber-400 flex-1">
                                  Instagram token expires in {daysLeft} day{daysLeft === 1 ? "" : "s"} ({tokenExpiresAt!.toLocaleDateString()}). Reconnect to keep publishing uninterrupted.
                                </span>
                                <button
                                  onClick={() => { window.location.href = `/api/oauth/instagram_business/authorize?wsId=${activeWorkspaceId}`; }}
                                  className="text-[10px] font-bold text-amber-400 border border-amber-400/40 rounded px-2 py-0.5 hover:bg-amber-400/10 transition-colors whitespace-nowrap"
                                  data-testid="button-reconnect-instagram-expiring"
                                >
                                  Reconnect
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      {key === "linkedin" && isConnected && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="linkedin-post-as-selector">
                          <span className="text-[10px] text-muted-foreground/60 font-semibold">Post as:</span>
                          <select
                            data-testid="select-linkedin-org"
                            value={linkedinOrgId}
                            onChange={async (e) => {
                              const val = e.target.value;
                              setLinkedinOrgId(val);
                              setSavingLinkedinOrg(true);
                              try {
                                await fetch(`/api/workspaces/${activeWorkspaceId}/linkedin/set-org`, {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  credentials: "include",
                                  body: JSON.stringify({ orgId: val || null }),
                                });
                                toast({ title: val ? "Now posting as Company Page" : "Now posting as personal account" });
                              } catch { toast({ title: "Failed to save", variant: "destructive" }); }
                              finally { setSavingLinkedinOrg(false); }
                            }}
                            className="text-[10px] bg-muted/50 border border-border rounded-md px-2 py-1 text-foreground"
                          >
                            <option value="">My Personal Account</option>
                            {(linkedinOrgsData?.organizations || []).map(org => (
                              <option key={org.orgId} value={org.orgId}>{org.name}</option>
                            ))}
                          </select>
                          {savingLinkedinOrg && <span className="text-[10px] text-muted-foreground/60">Saving…</span>}
                        </div>
                      )}
                      {key === "linkedin" && isConnected && scopeCheckData?.linkedinNeedsReconnect && (
                        <p className="text-[10px] text-amber-500/80 mt-1 flex items-center gap-1" data-testid="linkedin-reconnect-notice">
                          <span>⚠</span> Reconnect LinkedIn to enable company page posting with updated permissions.
                        </p>
                      )}
                      {key === "meta" && isConnected && scopeCheckData?.metaNeedsReconnect && (
                        <p className="text-[10px] text-amber-500/80 mt-1 flex items-center gap-1" data-testid="meta-reconnect-notice">
                          <span>⚠</span> Reconnect Meta to enable Instagram posting with updated permissions.
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      if (isConnected) {
                        const res = await fetch(`/api/oauth/${activeWorkspaceId}/${key}`, { method: "DELETE", credentials: "include" });
                        if (res.ok) {
                          queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
                          toast({ title: `${label} disconnected` });
                        }
                      } else {
                        const wsId = activeWorkspaceId;
                        window.location.href = `/api/oauth/${key}/authorize?wsId=${wsId}`;
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
                      isConnected
                        ? "bg-muted/50 text-muted-foreground border border-border hover:border-red-500/30 hover:text-red-400"
                        : `${bgColor} ${color} border ${borderColor} hover:opacity-80`
                    }`}
                    data-testid={`button-connect-${key}`}
                  >
                    {isConnected ? <><Unlink size={12} /> Disconnect</> : <><Link2 size={12} /> Connect</>}
                  </button>
                </div>
              );
            })}

            {/* WordPress Blog Connection */}
            {(() => {
              const isWpConnected = !!(workspace as any)?.wordpressUrl;
              return (
                <div
                  className={`p-3 md:p-4 rounded-xl border transition-all ${isWpConnected ? "bg-[#21759B]/10 border-[#21759B]/30" : "bg-muted/30 border-border"}`}
                  data-testid="channel-wordpress"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${isWpConnected ? "bg-[#21759B]/20" : "bg-muted/50"} flex items-center justify-center`}>
                        <Globe size={18} className={isWpConnected ? "text-[#21759B]" : "text-muted-foreground/50"} />
                      </div>
                      <div>
                        <p className={`text-sm font-medium ${isWpConnected ? "text-foreground" : "text-muted-foreground"}`}>WordPress</p>
                        <p className="text-[10px] text-muted-foreground/60">Blog publishing via Application Password — no plugin required</p>
                        {isWpConnected && (
                          <p className="text-[10px] text-green-400/60 mt-0.5 flex items-center gap-1">
                            <CheckCircle2 size={8} />
                            Connected: {(workspace as any).wordpressUrl}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isWpConnected && (
                        <button
                          onClick={() => saveWordPressCredentials(true)}
                          disabled={wpSaving || isViewer}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-muted/50 text-muted-foreground border border-border hover:border-red-500/30 hover:text-red-400 transition-all"
                          data-testid="button-disconnect-wordpress"
                        >
                          <Unlink size={12} /> Disconnect
                        </button>
                      )}
                    </div>
                  </div>
                  {!isWpConnected && (
                    <div className="mt-3 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Site URL</label>
                          <input
                            type="url"
                            placeholder="https://yourblog.com"
                            value={wpUrl}
                            onChange={e => setWpUrl(e.target.value)}
                            disabled={isViewer}
                            className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none"
                            data-testid="input-wordpress-url"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">WordPress Username</label>
                          <input
                            type="text"
                            placeholder="admin"
                            value={wpUsername}
                            onChange={e => setWpUsername(e.target.value)}
                            disabled={isViewer}
                            className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 outline-none"
                            data-testid="input-wordpress-username"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Application Password</label>
                        <div className="relative mt-1">
                          <input
                            type={wpShowPassword ? "text" : "password"}
                            placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                            value={wpAppPassword}
                            onChange={e => setWpAppPassword(e.target.value)}
                            disabled={isViewer}
                            className="w-full px-3 py-2 pr-10 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 outline-none"
                            data-testid="input-wordpress-app-password"
                          />
                          <button
                            type="button"
                            onClick={() => setWpShowPassword(v => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-muted-foreground"
                          >
                            {wpShowPassword ? <Unlink size={12} /> : <Key size={12} />}
                          </button>
                        </div>
                        <p className="text-[9px] text-muted-foreground/50 mt-1">Generate in WordPress → Users → Profile → Application Passwords. Write-only: never displayed after saving.</p>
                      </div>
                      <button
                        onClick={() => saveWordPressCredentials()}
                        disabled={wpSaving || !wpUrl || !wpUsername || isViewer}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#21759B]/20 text-[#21759B] border border-[#21759B]/30 hover:bg-[#21759B]/30 transition-all disabled:opacity-40"
                        data-testid="button-connect-wordpress"
                      >
                        {wpSaving ? <Loader2 size={12} className="animate-spin" /> : <Link2 size={12} />}
                        Connect WordPress
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        {/* WordPress connector plugin: one zip with this workspace's keys inside (owner / admin only) */}
        {(roleData?.role === "owner" || roleData?.role === "admin") && <WpConnectorDownload />}

        {/* Agentic mode: how each channel publishes once the Claude team's work passes its check */}
        {usesClaudeTeam(getAiEngine(workspace)) && (
          <PublishPolicySetting
            workspace={workspace}
            disabled={!!roleData?.role && roleData.role !== "owner" && roleData.role !== "admin"}
            disabledReason="Only a workspace owner or admin can change the publishing policy."
          />
        )}

        {/* Email: who your email comes from (platform / verified address / own domain), the CASL footer, and Rogers' sending policy */}
        {activeWorkspaceId && (
          <EmailSenderSetting
            workspaceId={activeWorkspaceId}
            disabled={!!roleData?.role && roleData.role !== "owner" && roleData.role !== "admin"}
            disabledReason="Only a workspace owner or admin can change the email settings."
          />
        )}

        <CalendarBookingSection workspaceId={activeWorkspaceId} workspace={workspace} isViewer={isViewer} />

        {/* Blog Publish Workflow */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-blog-publish-workflow">
          <div className="flex items-center gap-3 mb-2">
            <Globe size={20} className="text-[#21759B]" />
            <div>
              <h2 className="font-display font-bold text-foreground">Blog Publish Workflow</h2>
              <p className="text-xs text-muted-foreground/70 mt-0.5">Control whether Peggy's blog posts go straight to WordPress or wait for your approval first.</p>
            </div>
          </div>

          {/* Blog Generation On/Off Toggle */}
          <div className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-all ${blogConfig.blogGenerationEnabled ? "border-[#21759B]/30 bg-[#21759B]/5" : "border-destructive/30 bg-destructive/5"}`}>
            <div>
              <p className="text-sm font-semibold text-foreground">Autonomous Blog Generation</p>
              <p className="text-xs text-muted-foreground/70 mt-0.5">
                {blogConfig.blogGenerationEnabled
                  ? "Peggy automatically writes and queues blog posts daily. Turn off to stop all autonomous blog generation."
                  : "Blog generation is OFF — Peggy will not write or queue any autonomous blog posts."}
              </p>
            </div>
            <button
              data-testid="toggle-blog-generation-enabled"
              disabled={isViewer}
              onClick={() => setBlogConfig(v => ({ ...v, blogGenerationEnabled: !v.blogGenerationEnabled }))}
              className={`relative flex-shrink-0 w-12 h-6 rounded-full transition-colors duration-200 focus:outline-none ${blogConfig.blogGenerationEnabled ? "bg-[#21759B]" : "bg-muted-foreground/30"} disabled:opacity-40`}
            >
              <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${blogConfig.blogGenerationEnabled ? "translate-x-6" : "translate-x-0"}`} />
            </button>
          </div>
          {!blogConfig.blogGenerationEnabled && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
              <span className="font-semibold">Blogs are off.</span>
              <span>No blog posts will be generated automatically until you turn this back on. You can still trigger a blog manually from the Blogs tab.</span>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              data-testid="button-blog-workflow-auto-publish"
              onClick={() => setBlogConfig(v => ({ ...v, blogPublishWorkflow: "auto_publish" }))}
              disabled={isViewer}
              className={`text-left p-4 rounded-xl border transition-all ${blogConfig.blogPublishWorkflow === "auto_publish" ? "border-[#21759B]/50 bg-[#21759B]/10" : "border-border bg-muted/20 hover:border-border/70"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Zap size={14} className={blogConfig.blogPublishWorkflow === "auto_publish" ? "text-[#21759B]" : "text-muted-foreground"} />
                <span className="text-sm font-semibold text-foreground">Auto-Publish</span>
              </div>
              <p className="text-xs text-muted-foreground/70">Send straight to WordPress when SEO guardrails pass, once your account is live. Until then every post is saved as a draft for your approval.</p>
            </button>
            <button
              data-testid="button-blog-workflow-approval-required"
              onClick={() => setBlogConfig(v => ({ ...v, blogPublishWorkflow: "approval_required" }))}
              disabled={isViewer}
              className={`text-left p-4 rounded-xl border transition-all ${blogConfig.blogPublishWorkflow === "approval_required" ? "border-[#21759B]/50 bg-[#21759B]/10" : "border-border bg-muted/20 hover:border-border/70"}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Lock size={14} className={blogConfig.blogPublishWorkflow === "approval_required" ? "text-[#21759B]" : "text-muted-foreground"} />
                <span className="text-sm font-semibold text-foreground">Require Approval</span>
              </div>
              <p className="text-xs text-muted-foreground/70">Save as draft for you to review and approve before publishing to WordPress.</p>
            </button>
          </div>
          <button
            onClick={saveBlogConfig}
            disabled={blogConfigSaving || isViewer}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#21759B]/20 text-[#21759B] border border-[#21759B]/30 hover:bg-[#21759B]/30 transition-all disabled:opacity-40"
            data-testid="button-save-blog-workflow"
          >
            {blogConfigSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save Workflow Setting
          </button>
        </div>

        {/* Blog Guardrails */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-blog-guardrails">
          <div className="flex items-center gap-3 mb-2">
            <Globe size={20} className="text-[#21759B]" />
            <div>
              <h2 className="font-display font-bold text-foreground">Blog Guardrails</h2>
              <p className="text-xs text-muted-foreground/70 mt-0.5">Define SEO targets, author details, and content rules Peggy follows when writing blog posts.</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Author Name</label>
              <input
                type="text"
                data-testid="input-blog-author-name"
                placeholder="e.g. Sarah Mitchell"
                value={blogConfig.authorName}
                onChange={e => setBlogConfig(v => ({ ...v, authorName: e.target.value }))}
                disabled={isViewer}
                className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Primary Keyword</label>
              <input
                type="text"
                placeholder="e.g. digital marketing agency"
                value={blogConfig.primaryKeyword}
                onChange={e => setBlogConfig(v => ({ ...v, primaryKeyword: e.target.value }))}
                disabled={isViewer}
                className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none"
                data-testid="input-blog-primary-keyword"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Secondary Keywords</label>
              <input
                type="text"
                placeholder="e.g. social media strategy, content marketing"
                value={blogConfig.secondaryKeywords}
                onChange={e => setBlogConfig(v => ({ ...v, secondaryKeywords: e.target.value }))}
                disabled={isViewer}
                className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none"
                data-testid="input-blog-secondary-keywords"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Target Audience</label>
              <input
                type="text"
                placeholder="e.g. B2B marketing managers at SMBs"
                value={blogConfig.targetAudience}
                onChange={e => setBlogConfig(v => ({ ...v, targetAudience: e.target.value }))}
                disabled={isViewer}
                className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none"
                data-testid="input-blog-target-audience"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Content Pillars</label>
              <input
                type="text"
                placeholder="e.g. Lead generation, Brand storytelling, Industry insights"
                value={blogConfig.contentPillars}
                onChange={e => setBlogConfig(v => ({ ...v, contentPillars: e.target.value }))}
                disabled={isViewer}
                className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none"
                data-testid="input-blog-content-pillars"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Tone Instructions</label>
              <textarea
                placeholder="e.g. Professional but approachable. Use data-backed claims. Avoid corporate jargon."
                value={blogConfig.toneInstructions}
                onChange={e => setBlogConfig(v => ({ ...v, toneInstructions: e.target.value }))}
                disabled={isViewer}
                rows={2}
                className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none resize-none"
                data-testid="input-blog-tone-instructions"
              />
            </div>
          </div>
          {/* Min SEO Score slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Minimum SEO Score before Publishing</label>
              <span className="text-sm font-bold text-[#21759B]" data-testid="text-min-seo-score">{blogConfig.minSeoScore}/100</span>
            </div>
            <input
              type="range"
              min={70}
              max={100}
              value={blogConfig.minSeoScore}
              onChange={e => setBlogConfig(v => ({ ...v, minSeoScore: Number(e.target.value) }))}
              disabled={isViewer}
              className="w-full accent-[#21759B]"
              data-testid="slider-min-seo-score"
            />
            <div className="flex justify-between text-[9px] text-muted-foreground/50">
              <span>70 — relaxed</span>
              <span>95 — recommended</span>
              <span>100 — perfect</span>
            </div>
            <p className="text-[10px] text-muted-foreground/60">Peggy will revise the post up to 3 times to hit this score. Posts that can't reach the threshold are flagged "Needs Review" instead of published.</p>
          </div>

          {/* Rank Math SEO Fields */}
          <div className="border-t border-border/50 pt-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">Rank Math SEO (optional)</span>
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-400 border border-violet-500/20">Rank Math Plugin</span>
            </div>
            <p className="text-[10px] text-muted-foreground/50">These fields are injected as Rank Math meta when publishing to WordPress. Leave blank to let Peggy generate them automatically.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">SEO Title Template</label>
                <input
                  type="text"
                  data-testid="input-rankmath-title"
                  placeholder="e.g. %title% | %sitename%"
                  value={blogConfig.rankMathTitle}
                  onChange={e => setBlogConfig(v => ({ ...v, rankMathTitle: e.target.value }))}
                  disabled={isViewer}
                  className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-violet-500/40 focus:border-violet-500/40 outline-none"
                />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Focus Keyword</label>
                <input
                  type="text"
                  data-testid="input-rankmath-focus-keyword"
                  placeholder="e.g. digital marketing strategy"
                  value={blogConfig.rankMathFocusKeyword}
                  onChange={e => setBlogConfig(v => ({ ...v, rankMathFocusKeyword: e.target.value }))}
                  disabled={isViewer}
                  className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-violet-500/40 focus:border-violet-500/40 outline-none"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Meta Description Template</label>
                <textarea
                  data-testid="input-rankmath-description"
                  placeholder="e.g. Discover expert tips on %keyword%. Read our guide to..."
                  value={blogConfig.rankMathDescription}
                  onChange={e => setBlogConfig(v => ({ ...v, rankMathDescription: e.target.value }))}
                  disabled={isViewer}
                  rows={2}
                  className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-violet-500/40 focus:border-violet-500/40 outline-none resize-none"
                />
              </div>
            </div>
          </div>

          {/* WordPress Category & Tag IDs */}
          <div className="border-t border-border/50 pt-5 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60">WordPress Taxonomy</span>
            </div>
            <p className="text-[10px] text-muted-foreground/50">Enter WordPress category and tag IDs (comma-separated integers). Find IDs in WordPress → Posts → Categories/Tags. Posts 1 = Uncategorized.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className='text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide'>Category IDs</label>
                <div className='flex gap-2 mt-1 relative'>
                  <input
                    type='text'
                    data-testid='input-wp-category-ids'
                    placeholder='e.g. 5, 12, 23'
                    value={blogConfig.categoryIds}
                    onChange={e => setBlogConfig(v => ({ ...v, categoryIds: e.target.value }))}
                    disabled={isViewer}
                    className='flex-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none'
                  />
                  {!!(workspace as any)?.wordpressUrl && (
                    <button type='button' onClick={() => browseTaxonomy('categories')} disabled={wpTaxonomyLoading.categories || isViewer}
                      className='px-2 py-1.5 text-xs rounded-lg bg-[#21759B]/10 text-[#21759B] border border-[#21759B]/30 hover:bg-[#21759B]/20 transition-all disabled:opacity-40 whitespace-nowrap flex-shrink-0'>
                      {wpTaxonomyLoading.categories ? '...' : 'Browse'}
                    </button>
                  )}
                  {wpBrowseOpen.categories && (
                    <div className='absolute top-full left-0 z-50 mt-1 w-72 max-h-60 overflow-y-auto bg-card border border-border rounded-xl shadow-xl p-2 space-y-0.5'>
                      <div className='flex justify-between items-center px-1 pb-1 border-b border-border/50 mb-1'>
                        <span className='text-[10px] font-bold text-muted-foreground uppercase'>Select categories</span>
                        <button type='button' onClick={() => setWpBrowseOpen(p=>({...p,categories:false}))} className='text-muted-foreground hover:text-foreground text-xs px-1'>✕</button>
                      </div>
                      {wpTaxonomy.categories.length === 0
                        ? <p className='text-xs text-muted-foreground px-2 py-1'>No categories found</p>
                        : wpTaxonomy.categories.map(c => {
                            const selected = blogConfig.categoryIds.split(',').map((s:string)=>s.trim()).includes(String(c.id));
                            return (
                              <button key={c.id} type='button' onClick={() => toggleTaxonomyId('categoryIds', c.id)}
                                className={`w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-colors ${ selected ? 'bg-[#21759B]/10 text-[#21759B]' : 'hover:bg-muted/50 text-foreground' }`}>
                                <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${ selected ? 'bg-[#21759B] border-[#21759B]' : 'border-border' }`}>{selected && <span className='text-white text-[8px]'>✓</span>}</span>
                                <span className='flex-1 truncate'>{c.name}</span>
                                <span className='text-muted-foreground/50 text-[10px] flex-shrink-0'>{c.count}</span>
                              </button>
                            );
                          })}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className='text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide'>Tag IDs</label>
                <div className='flex gap-2 mt-1 relative'>
                  <input
                    type='text'
                    data-testid='input-wp-tag-ids'
                    placeholder='e.g. 8, 15'
                    value={blogConfig.tagIds}
                    onChange={e => setBlogConfig(v => ({ ...v, tagIds: e.target.value }))}
                    disabled={isViewer}
                    className='flex-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#21759B]/50 focus:border-[#21759B]/50 outline-none'
                  />
                  {!!(workspace as any)?.wordpressUrl && (
                    <button type='button' onClick={() => browseTaxonomy('tags')} disabled={wpTaxonomyLoading.tags || isViewer}
                      className='px-2 py-1.5 text-xs rounded-lg bg-[#21759B]/10 text-[#21759B] border border-[#21759B]/30 hover:bg-[#21759B]/20 transition-all disabled:opacity-40 whitespace-nowrap flex-shrink-0'>
                      {wpTaxonomyLoading.tags ? '...' : 'Browse'}
                    </button>
                  )}
                  {wpBrowseOpen.tags && (
                    <div className='absolute top-full left-0 z-50 mt-1 w-72 max-h-60 overflow-y-auto bg-card border border-border rounded-xl shadow-xl p-2 space-y-0.5'>
                      <div className='flex justify-between items-center px-1 pb-1 border-b border-border/50 mb-1'>
                        <span className='text-[10px] font-bold text-muted-foreground uppercase'>Select tags</span>
                        <button type='button' onClick={() => setWpBrowseOpen(p=>({...p,tags:false}))} className='text-muted-foreground hover:text-foreground text-xs px-1'>✕</button>
                      </div>
                      {wpTaxonomy.tags.length === 0
                        ? <p className='text-xs text-muted-foreground px-2 py-1'>No tags found</p>
                        : wpTaxonomy.tags.map(t => {
                            const selected = blogConfig.tagIds.split(',').map((s:string)=>s.trim()).includes(String(t.id));
                            return (
                              <button key={t.id} type='button' onClick={() => toggleTaxonomyId('tagIds', t.id)}
                                className={`w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center gap-2 transition-colors ${ selected ? 'bg-[#21759B]/10 text-[#21759B]' : 'hover:bg-muted/50 text-foreground' }`}>
                                <span className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${ selected ? 'bg-[#21759B] border-[#21759B]' : 'border-border' }`}>{selected && <span className='text-white text-[8px]'>✓</span>}</span>
                                <span className='flex-1 truncate'>{t.name}</span>
                                <span className='text-muted-foreground/50 text-[10px] flex-shrink-0'>{t.count}</span>
                              </button>
                            );
                          })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={saveBlogConfig}
            disabled={blogConfigSaving || isViewer}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#21759B]/20 text-[#21759B] border border-[#21759B]/30 hover:bg-[#21759B]/30 transition-all disabled:opacity-40"
            data-testid="button-save-blog-guardrails"
          >
            {blogConfigSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            Save Blog Guardrails
          </button>
        </div>

        {/* Cloudflare Cache Purge */}
        {(() => {
          const isCfConnected = !!cfZoneId;
          return (
            <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="section-cloudflare">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-[#F48120]/10 flex items-center justify-center">
                  <span className="text-[#F48120] text-sm font-bold">CF</span>
                </div>
                <div>
                  <h2 className="font-display font-bold text-foreground">Cloudflare Cache Purge</h2>
                  <p className="text-xs text-muted-foreground/70 mt-0.5">Automatically purge Cloudflare cache after each WordPress publish so readers see fresh content immediately.</p>
                </div>
              </div>
              {isCfConnected && (
                <div className="flex items-center gap-2 text-[10px] text-emerald-400">
                  <CheckCircle2 size={10} />
                  Zone configured — cache will be purged on each publish
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">Zone ID</label>
                  <input
                    type="text"
                    data-testid="input-cloudflare-zone-id"
                    placeholder="e.g. 023e105f4ecef8ad9ca31a8372d0c353"
                    value={cfZoneId}
                    onChange={e => setCfZoneId(e.target.value)}
                    disabled={isViewer}
                    className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#F48120]/40 outline-none font-mono"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground/70 font-medium uppercase tracking-wide">API Token</label>
                  <input
                    type="password"
                    data-testid="input-cloudflare-api-token"
                    placeholder="Cloudflare API token with Cache Purge permission"
                    value={cfApiToken}
                    onChange={e => setCfApiToken(e.target.value)}
                    disabled={isViewer}
                    className="w-full mt-1 px-3 py-2 text-sm bg-muted/50 border border-border rounded-lg text-foreground placeholder:text-muted-foreground/40 focus:ring-1 focus:ring-[#F48120]/40 outline-none"
                  />
                  <p className="text-[9px] text-muted-foreground/50 mt-1">Write-only — never displayed after saving. Create a token with "Cache Purge" permission in Cloudflare dashboard.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => saveCloudflareSettings()}
                  disabled={cfSaving || !cfZoneId || isViewer}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold bg-[#F48120]/20 text-[#F48120] border border-[#F48120]/30 hover:bg-[#F48120]/30 transition-all disabled:opacity-40"
                  data-testid="button-save-cloudflare"
                >
                  {cfSaving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                  Save Cloudflare Settings
                </button>
                {isCfConnected && (
                  <button
                    onClick={() => saveCloudflareSettings(true)}
                    disabled={cfSaving || isViewer}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-muted/50 text-muted-foreground border border-border hover:border-red-500/30 hover:text-red-400 transition-all"
                    data-testid="button-disconnect-cloudflare"
                  >
                    <Unlink size={12} /> Disconnect
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        </TabsContent>

        <TabsContent value="agents" className="space-y-6 mt-6">
        {/* AI engine — which brain answers Draper chat */}
        <AiEngineSetting
          workspaceId={activeWorkspaceId}
          currentEngine={getAiEngine(workspace)}
          disabled={!!roleData?.role && roleData.role !== "owner" && roleData.role !== "admin"}
          disabledReason="Only a workspace owner or admin can change the AI engine."
        />

        {/* Business knowledge — the document the Claude work team loads before every job */}
        {activeWorkspaceId && (
          <BusinessKnowledgeSetting
            workspaceId={activeWorkspaceId}
            canEdit={!roleData?.role || roleData.role === "owner" || roleData.role === "admin"}
          />
        )}

        {/* Rogers Nurture Goal */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-rogers-nurture-goal">
          <div className="flex items-center gap-3 mb-2">
            <Bot size={20} className="text-emerald-400" />
            <h2 className="font-display font-bold text-foreground">Rogers — Default Nurture Goal</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Sets the primary objective Rogers uses when writing nurture sequences. All new lead sequences will default to this goal unless overridden per-lead.</p>
          <div className="space-y-3">
            {[
              { value: "appointment", label: "Book Appointment", desc: "Rogers focuses on scheduling a call or demo with each lead." },
              { value: "awareness", label: "Build Awareness", desc: "Educate leads about your brand, product, or service over time." },
              { value: "coupon", label: "Drive Purchase (Coupon)", desc: "Move leads toward a transaction using an offer or discount." },
              { value: "email_list_growth", label: "Grow Email List", desc: "Encourage opt-in and deeper engagement with your email program." },
            ].map(({ value, label, desc }) => (
              <button
                key={value}
                data-testid={`button-nurture-goal-${value}`}
                onClick={() => setNurtureGoal(value)}
                className={`w-full text-left px-4 py-3 rounded-xl border transition-all ${nurtureGoal === value ? "border-emerald-500/40 bg-emerald-500/10" : "border-border bg-muted/20 hover:border-border/70"}`}
              >
                <div className="text-sm font-semibold text-foreground">{label}</div>
                <div className="text-[11px] text-muted-foreground/70 mt-0.5">{desc}</div>
              </button>
            ))}
          </div>
          <button
            data-testid="button-save-nurture-goal"
            onClick={async () => {
              if (!activeWorkspaceId) return;
              setSavingNurtureGoal(true);
              try {
                await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/nurture-goal`, { defaultNurtureGoal: nurtureGoal });
                queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
                toast({ title: "Nurture goal saved" });
              } catch { toast({ title: "Save failed", variant: "destructive" }); }
              finally { setSavingNurtureGoal(false); }
            }}
            disabled={savingNurtureGoal || isViewer}
            title={isViewer ? "You need Editor or Owner access." : undefined}
            className="flex items-center gap-2 px-4 py-3 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-xl text-sm font-medium hover:bg-emerald-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {savingNurtureGoal ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Nurture Goal
          </button>
        </div>

        {/* Audit Log */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="section-audit-log">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Clock size={20} className="text-muted-foreground/70" />
              <h2 className="font-display font-bold text-foreground">Agent Audit Log</h2>
            </div>
            <button
              data-testid="button-load-audit-log"
              onClick={async () => {
                if (!activeWorkspaceId) return;
                setShowAuditLog(true);
                setLoadingAuditLogs(true);
                try {
                  const res = await fetch(`/api/workspaces/${activeWorkspaceId}/audit-log?limit=50`, { credentials: "include" });
                  if (res.ok) { const data = await res.json(); setAuditLogs(data.logs || data || []); }
                } catch { /* noop */ } finally { setLoadingAuditLogs(false); }
              }}
              className="flex items-center gap-2 px-4 py-2 border border-border rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <RefreshCw size={14} className={loadingAuditLogs ? "animate-spin" : ""} />
              {showAuditLog ? "Refresh" : "Load Logs"}
            </button>
          </div>
          <p className="text-xs text-muted-foreground/70">Every agent action, tool call, and decision is logged here for full transparency and debugging.</p>
          {showAuditLog && (
            <div className="space-y-2 max-h-96 overflow-y-auto" data-testid="audit-log-list">
              {loadingAuditLogs ? (
                <div className="flex items-center gap-2 text-muted-foreground/70 text-sm py-4">
                  <Loader2 size={14} className="animate-spin" /> Loading audit log...
                </div>
              ) : auditLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground/60 py-4 text-center" data-testid="audit-log-empty">No agent actions recorded yet.</p>
              ) : auditLogs.map((log: any, i: number) => (
                <div key={log.id || i} className="flex items-start gap-3 px-3 py-2.5 rounded-xl border border-border bg-muted/10" data-testid={`audit-log-entry-${log.id || i}`}>
                  <div className="shrink-0 mt-0.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase ${log.level === "error" ? "bg-red-500/15 text-red-400" : log.level === "warn" ? "bg-amber-500/15 text-amber-400" : "bg-blue-500/15 text-blue-400"}`}>{log.agent || log.level || "info"}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-foreground/80 leading-snug">{log.action || log.message || JSON.stringify(log)}</p>
                    {log.createdAt && <p className="text-[10px] text-muted-foreground/50 mt-0.5">{new Date(log.createdAt).toLocaleString()}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-agent-autonomy">
          <div className="flex items-center gap-3 mb-2">
            <Bot size={20} className="text-blue-400" />
            <h2 className="font-display font-bold text-foreground">Agent Autonomy & Scheduling</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Configure how often the AI agents run and whether they can auto-publish content without manual approval.</p>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-3">
              <span className="flex items-center gap-1.5"><Clock size={12} /> Agent Schedule</span>
            </label>
            <div className="flex flex-wrap gap-3" data-testid="select-agent-schedule">
              {[
                { value: "manual", label: "Manual" },
                { value: "daily", label: "Daily" },
                { value: "weekly", label: "Weekly" },
              ].map(({ value, label: optLabel }) => (
                <label key={value} className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${scheduleConfig.schedule === value ? "bg-blue-600/20 border-blue-500/30 text-blue-400" : "bg-muted/30 border-border text-muted-foreground"}`}>
                  <input
                    type="radio"
                    name="agent-schedule"
                    value={value}
                    checked={scheduleConfig.schedule === value}
                    onChange={(e) => setScheduleConfig({ ...scheduleConfig, schedule: e.target.value })}
                    className="accent-blue-500"
                  />
                  <span className="text-sm font-medium">{optLabel}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-3 rounded-2xl border border-purple-500/20 bg-purple-500/5 p-4" data-testid="section-autonomous-mode">
            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-purple-400" />
              <p className="text-xs font-bold text-purple-300 uppercase tracking-wider">Autonomous Mode</p>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Auto-Publish (Global)</p>
                <p className="text-[10px] text-muted-foreground/60">Master switch for autonomous publishing. When on, use per-platform toggles below to control which platforms auto-publish.</p>
              </div>
              <button
                data-testid="toggle-auto-publish"
                onClick={() => setScheduleConfig({ ...scheduleConfig, autoPublish: !scheduleConfig.autoPublish })}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${scheduleConfig.autoPublish ? "bg-purple-600" : "bg-muted"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${scheduleConfig.autoPublish ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>
            {scheduleConfig.autoPublish && (
              <div className="ml-4 space-y-2 border-l-2 border-purple-500/20 pl-3">
                <p className="text-[10px] text-muted-foreground/50 uppercase tracking-wider font-bold">Per-Platform Auto-Publish</p>
                {[
                  { key: "linkedin", label: "LinkedIn", Icon: SiLinkedin },
                  { key: "instagram", label: "Instagram", Icon: SiInstagram },
                  { key: "facebook", label: "Facebook", Icon: SiFacebook },
                  { key: "x", label: "X (Twitter)", Icon: SiX },
                ].map(({ key, label, Icon }) => (
                  <div key={key} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon size={12} className="text-muted-foreground" />
                      <p className="text-xs font-medium text-foreground">{label}</p>
                    </div>
                    <button
                      data-testid={`toggle-auto-publish-${key}`}
                      onClick={() => setScheduleConfig({ ...scheduleConfig, autoPublishPlatforms: { ...scheduleConfig.autoPublishPlatforms, [key]: !scheduleConfig.autoPublishPlatforms[key] } })}
                      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${scheduleConfig.autoPublishPlatforms[key] ? "bg-purple-600" : "bg-muted"}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${scheduleConfig.autoPublishPlatforms[key] ? "translate-x-4" : "translate-x-0"}`} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-foreground">Auto-Approve Peggy's Content</p>
                <p className="text-[10px] text-muted-foreground/60">Content Peggy creates skips the approval queue and goes straight to Scheduled. Turn off to review every post before it can publish.</p>
              </div>
              <button
                data-testid="toggle-auto-approve-peggy"
                onClick={() => setScheduleConfig({ ...scheduleConfig, autoApprovePeggyContent: !scheduleConfig.autoApprovePeggyContent })}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${scheduleConfig.autoApprovePeggyContent ? "bg-purple-600" : "bg-muted"}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${scheduleConfig.autoApprovePeggyContent ? "translate-x-5" : "translate-x-0"}`} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Last Agent Run</span>
              <span className="text-sm text-muted-foreground" data-testid="text-last-run">
                {scheduleConfig.lastRun ? new Date(scheduleConfig.lastRun).toLocaleString() : "Never"}
              </span>
            </div>
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Next Scheduled Run</span>
              <span className="text-sm text-muted-foreground" data-testid="text-next-run">
                {scheduleConfig.nextRun ? new Date(scheduleConfig.nextRun).toLocaleString() : "Not scheduled"}
              </span>
            </div>
          </div>

          <button
            data-testid="button-save-schedule"
            onClick={async () => {
              setSavingSchedule(true);
              try {
                await fetch(`/api/workspaces/${activeWorkspaceId}/schedule-config`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ schedule: scheduleConfig.schedule, autoPublish: scheduleConfig.autoPublish, autoApprovePeggyContent: scheduleConfig.autoApprovePeggyContent, autoPublishPlatforms: scheduleConfig.autoPublishPlatforms }),
                  credentials: "include",
                });
                toast({ title: "Schedule settings saved" });
              } catch {
                toast({ title: "Failed to save schedule", variant: "destructive" });
              } finally {
                setSavingSchedule(false);
              }
            }}
            disabled={savingSchedule || isViewer}
            title={isViewer ? "You need Editor or Owner access." : undefined}
            className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {savingSchedule ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Schedule
          </button>
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-agent-levels">
          <div className="flex items-center gap-3 mb-2">
            <Zap size={20} className="text-emerald-400" />
            <h2 className="font-display font-bold text-foreground">Agent Autonomy Levels</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Control how independently each agent operates. Manual requires approval for everything. Smart auto-executes high-confidence actions. Always On runs fully autonomous.</p>

          <div className="space-y-4">
            {[
              { key: "draper", name: "Draper", role: "Strategist", color: "blue" },
              { key: "peggy", name: "Peggy", role: "Content Creator", color: "purple" },
              { key: "pete", name: "Pete", role: "Ad Manager", color: "amber" },
              { key: "rogers", name: "Rogers", role: "Lead Closer", color: "emerald" },
            ].map((agent) => {
              const config = agentAutonomy[agent.key] || { autonomyLevel: "manual", confidenceThreshold: 80 };
              const colorMap: Record<string, { bg: string; border: string; text: string }> = {
                blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400" },
                purple: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400" },
                amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400" },
                emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400" },
              };
              const c = colorMap[agent.color];
              return (
                <div key={agent.key} className={`${c.bg} border ${c.border} rounded-xl p-4 space-y-3`} data-testid={`agent-autonomy-${agent.key}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Bot size={16} className={c.text} />
                      <span className="text-sm font-bold text-foreground">{agent.name}</span>
                      <span className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">{agent.role}</span>
                    </div>
                    <div className="flex gap-1" data-testid={`autonomy-level-${agent.key}`}>
                      {(["manual", "smart", "always_on"] as const).map((level) => (
                        <button
                          key={level}
                          onClick={() => setAgentAutonomy(prev => ({ ...prev, [agent.key]: { ...prev[agent.key], autonomyLevel: level } }))}
                          data-testid={`button-autonomy-${agent.key}-${level}`}
                          className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wide transition-all ${
                            config.autonomyLevel === level
                              ? `${c.bg} ${c.text} border ${c.border}`
                              : "bg-card/50 text-muted-foreground/70 border border-border hover:text-muted-foreground"
                          }`}
                        >
                          {level === "always_on" ? "Always On" : level.charAt(0).toUpperCase() + level.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  {config.autonomyLevel === "smart" && (
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-muted-foreground/70 shrink-0 w-28">Confidence: {config.confidenceThreshold}%</span>
                      <input
                        type="range"
                        min="50"
                        max="100"
                        value={config.confidenceThreshold}
                        onChange={(e) => setAgentAutonomy(prev => ({ ...prev, [agent.key]: { ...prev[agent.key], confidenceThreshold: Number(e.target.value) } }))}
                        className="flex-1 accent-blue-500 h-1"
                        data-testid={`slider-confidence-${agent.key}`}
                      />
                    </div>
                  )}
                  <p className="text-[10px] text-muted-foreground/50">
                    {config.autonomyLevel === "manual" && "All actions require your approval before execution."}
                    {config.autonomyLevel === "smart" && `Actions above ${config.confidenceThreshold}% confidence auto-execute. Below threshold queued for approval.`}
                    {config.autonomyLevel === "always_on" && "Fully autonomous. Actions execute immediately with post-notification."}
                  </p>
                </div>
              );
            })}
          </div>

          <button
            data-testid="button-save-autonomy"
            onClick={async () => {
              setSavingAutonomy(true);
              try {
                await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}`, { agentConfig: agentAutonomy });
                queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
                toast({ title: "Agent autonomy settings saved" });
              } catch {
                toast({ title: "Failed to save autonomy settings", variant: "destructive" });
              } finally {
                setSavingAutonomy(false);
              }
            }}
            disabled={savingAutonomy || isViewer}
            title={isViewer ? "You need Editor or Owner access." : undefined}
            className="flex items-center gap-2 px-4 py-3 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-xl text-sm font-medium hover:bg-emerald-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {savingAutonomy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Autonomy Settings
          </button>
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-auto-generation">
          <div className="flex items-center gap-3 mb-2">
            <Sparkles size={20} className="text-purple-400" />
            <h2 className="font-display font-bold text-foreground">Schedule Auto-Generation</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Configure Peggy to automatically generate content on a schedule. Generated content goes to your approval queue.</p>

          <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Enable Auto-Generation</p>
              <p className="text-[10px] text-muted-foreground/60">Peggy will generate content based on your strategy</p>
            </div>
            <button
              data-testid="toggle-auto-gen"
              onClick={() => setAutoGenConfig({ ...autoGenConfig, enabled: !autoGenConfig.enabled })}
              className={`relative w-11 h-6 rounded-full transition-colors ${autoGenConfig.enabled ? "bg-purple-600" : "bg-muted"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${autoGenConfig.enabled ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          {autoGenConfig.enabled && (
            <>
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-3">
                  <span className="flex items-center gap-1.5"><Calendar size={12} /> Generation Frequency</span>
                </label>
                <div className="flex flex-wrap gap-3" data-testid="select-gen-frequency">
                  {[
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                    { value: "biweekly", label: "Bi-Weekly" },
                  ].map(({ value, label: optLabel }) => (
                    <label key={value} className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${autoGenConfig.frequency === value ? "bg-purple-600/20 border-purple-500/30 text-purple-400" : "bg-muted/30 border-border text-muted-foreground"}`}>
                      <input
                        type="radio"
                        name="gen-frequency"
                        value={value}
                        checked={autoGenConfig.frequency === value}
                        onChange={(e) => setAutoGenConfig({ ...autoGenConfig, frequency: e.target.value })}
                        className="accent-purple-500"
                        data-testid={`radio-gen-frequency-${value}`}
                      />
                      <span className="text-sm font-medium">{optLabel}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-3">Content Types</label>
                <div className="flex flex-wrap gap-2" data-testid="select-gen-content-types">
                  {["Post", "Carousel", "Story", "Lead Magnet"].map((type) => (
                    <label key={type} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs font-medium ${autoGenConfig.contentTypes.includes(type) ? "bg-purple-600/20 border-purple-500/30 text-purple-400" : "bg-muted/30 border-border text-muted-foreground"}`}>
                      <input
                        type="checkbox"
                        checked={autoGenConfig.contentTypes.includes(type)}
                        onChange={() => {
                          const types = autoGenConfig.contentTypes.includes(type)
                            ? autoGenConfig.contentTypes.filter(t => t !== type)
                            : [...autoGenConfig.contentTypes, type];
                          setAutoGenConfig({ ...autoGenConfig, contentTypes: types });
                        }}
                        className="accent-purple-500 w-3 h-3"
                        data-testid={`checkbox-gen-type-${type.toLowerCase().replace(/\s+/g, "-")}`}
                      />
                      {type}
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Posts Per Run</label>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="input-posts-per-run"
                    type="number"
                    min="1"
                    max="20"
                    value={autoGenConfig.postsPerRun}
                    onChange={(e) => setAutoGenConfig({ ...autoGenConfig, postsPerRun: Math.max(1, Math.min(20, Number(e.target.value))) })}
                    className="w-20 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-purple-500 outline-none text-foreground text-center"
                  />
                  <span className="text-xs text-muted-foreground/70">content items per generation cycle</span>
                </div>
              </div>
            </>
          )}

          <Button
            data-testid="button-save-auto-gen"
            onClick={async () => {
              setSavingAutoGen(true);
              try {
                await fetch(`/api/workspaces/${activeWorkspaceId}/schedule-config`, {
                  method: "PUT",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ autoGeneration: autoGenConfig }),
                  credentials: "include",
                });
                toast({ title: "Auto-generation settings saved" });
              } catch {
                toast({ title: "Failed to save auto-generation settings", variant: "destructive" });
              } finally {
                setSavingAutoGen(false);
              }
            }}
            disabled={savingAutoGen || isViewer}
            title={isViewer ? "You need Editor or Owner access." : undefined}
            variant="outline"
            className="border-purple-500/30 text-purple-400 disabled:pointer-events-none"
          >
            {savingAutoGen ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
            Save Auto-Generation
          </Button>
        </div>

        {/* Draper Knowledge Doc */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-draper-knowledge">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Bot size={20} className="text-blue-400" />
              <h2 className="font-display font-bold text-foreground">Draper's Business Knowledge</h2>
            </div>
            {!kdEditMode && (
              <button
                data-testid="button-edit-knowledge"
                onClick={() => {
                  setKdForm({
                    brandVoice: knowledgeDoc?.brandVoice || "",
                    industry: knowledgeDoc?.industry || "",
                    targetAudience: knowledgeDoc?.targetAudience || "",
                    currentGoals: knowledgeDoc?.currentGoals || "",
                    strategySnapshot: knowledgeDoc?.strategySnapshot || "",
                  });
                  const cp = knowledgeDoc?.contentPreferences as { platforms?: string[]; postLength?: string; formats?: string[]; topicsToAvoid?: string[]; preferredPostingDays?: string[] } | null | undefined;
                  setKdContentPrefs({
                    platforms: cp?.platforms?.join(", ") || "",
                    postLength: cp?.postLength || "",
                    formats: cp?.formats?.join(", ") || "",
                    topicsToAvoid: cp?.topicsToAvoid?.join(", ") || "",
                    preferredPostingDays: cp?.preferredPostingDays?.join(", ") || "",
                  });
                  setKdEditMode(true);
                }}
                disabled={isViewer}
                title={isViewer ? "You need Editor or Owner access." : undefined}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600/10 text-blue-400 border border-blue-500/30 rounded-xl text-xs font-medium hover:bg-blue-600/20 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                <Pencil size={13} /> Edit
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground/70">This is the knowledge base Draper injects into every prompt. It's auto-populated from your brand kit and strategy, and updated whenever you tell Draper things in chat.</p>

          {kdLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground/60"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : kdEditMode ? (
            <div className="space-y-4">
              {([
                { key: "industry", label: "Industry", placeholder: "e.g. Digital Marketing Agency" },
                { key: "brandVoice", label: "Brand Voice", placeholder: "e.g. Professional, witty, confident" },
                { key: "targetAudience", label: "Target Audience", placeholder: "e.g. SMB owners in North America" },
                { key: "currentGoals", label: "Current Goals", placeholder: "e.g. Grow LinkedIn following by 30% in Q2" },
                { key: "strategySnapshot", label: "Strategy Overview", placeholder: "Brief summary of the current marketing approach" },
              ] as { key: keyof typeof kdForm; label: string; placeholder: string }[]).map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
                  <textarea
                    data-testid={`input-knowledge-${key}`}
                    rows={2}
                    placeholder={placeholder}
                    value={kdForm[key]}
                    onChange={(e) => setKdForm({ ...kdForm, [key]: e.target.value })}
                    className="w-full bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:border-blue-500 outline-none text-foreground resize-none"
                  />
                </div>
              ))}
              <div className="pt-2 border-t border-border/50">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Content Preferences</div>
                {([
                  { key: "platforms" as const, label: "Platforms", placeholder: "e.g. LinkedIn, Instagram, Twitter (comma-separated)" },
                  { key: "postLength" as const, label: "Post Length", placeholder: "e.g. short, medium, long" },
                  { key: "formats" as const, label: "Content Formats", placeholder: "e.g. carousel, video, text (comma-separated)" },
                  { key: "topicsToAvoid" as const, label: "Topics to Avoid", placeholder: "e.g. politics, competitors (comma-separated)" },
                  { key: "preferredPostingDays" as const, label: "Preferred Posting Days", placeholder: "e.g. Monday, Wednesday, Friday (comma-separated)" },
                ]).map(({ key, label, placeholder }) => (
                  <div key={key} className="mb-3">
                    <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
                    <input
                      data-testid={`input-contentpref-${key}`}
                      type="text"
                      placeholder={placeholder}
                      value={kdContentPrefs[key]}
                      onChange={(e) => setKdContentPrefs({ ...kdContentPrefs, [key]: e.target.value })}
                      className="w-full bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:border-blue-500 outline-none text-foreground"
                    />
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="button-save-knowledge"
                  disabled={kdSaving}
                  onClick={async () => {
                    if (!activeWorkspaceId) return;
                    setKdSaving(true);
                    try {
                      const splitList = (s: string) => s.split(",").map(x => x.trim()).filter(Boolean);
                      const contentPreferences = {
                        platforms: splitList(kdContentPrefs.platforms),
                        postLength: kdContentPrefs.postLength.trim() || undefined,
                        formats: splitList(kdContentPrefs.formats),
                        topicsToAvoid: splitList(kdContentPrefs.topicsToAvoid),
                        preferredPostingDays: splitList(kdContentPrefs.preferredPostingDays),
                      };
                      await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/knowledge`, { ...kdForm, contentPreferences });
                      await kdRefetch();
                      setKdEditMode(false);
                      toast({ title: "Knowledge updated" });
                    } catch { toast({ title: "Save failed", variant: "destructive" }); }
                    finally { setKdSaving(false); }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                >
                  {kdSaving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                </button>
                <button
                  data-testid="button-cancel-knowledge"
                  onClick={() => setKdEditMode(false)}
                  className="px-4 py-2 text-muted-foreground border border-border rounded-xl text-sm font-medium hover:bg-muted/30 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {knowledgeDoc ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { label: "Industry", value: knowledgeDoc.industry },
                      { label: "Brand Voice", value: knowledgeDoc.brandVoice },
                      { label: "Target Audience", value: knowledgeDoc.targetAudience },
                      { label: "Current Goals", value: knowledgeDoc.currentGoals },
                      { label: "Strategy Overview", value: knowledgeDoc.strategySnapshot },
                    ].map(({ label, value }) => value ? (
                      <div key={label} className="bg-muted/20 rounded-xl px-3 py-2.5">
                        <div className="text-[10px] text-muted-foreground/60 uppercase tracking-wide mb-0.5">{label}</div>
                        <div className="text-sm text-foreground" data-testid={`text-knowledge-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</div>
                      </div>
                    ) : null)}
                  </div>
                  {(() => {
                    const cp = knowledgeDoc.contentPreferences as { platforms?: string[]; postLength?: string; formats?: string[]; topicsToAvoid?: string[]; preferredPostingDays?: string[] } | null | undefined;
                    const cpParts: string[] = [];
                    if (cp?.platforms?.length) cpParts.push(`Platforms: ${cp.platforms.join(", ")}`);
                    if (cp?.postLength) cpParts.push(`Post Length: ${cp.postLength}`);
                    if (cp?.formats?.length) cpParts.push(`Formats: ${cp.formats.join(", ")}`);
                    if (cp?.topicsToAvoid?.length) cpParts.push(`Avoid: ${cp.topicsToAvoid.join(", ")}`);
                    if (cp?.preferredPostingDays?.length) cpParts.push(`Posting Days: ${cp.preferredPostingDays.join(", ")}`);
                    if (cpParts.length === 0) return null;
                    return (
                      <div className="bg-muted/20 rounded-xl px-3 py-2.5" data-testid="text-knowledge-content-preferences">
                        <div className="text-[10px] text-muted-foreground/60 uppercase tracking-wide mb-1">Content Preferences</div>
                        <div className="text-sm text-foreground space-y-0.5">
                          {cpParts.map(part => <div key={part}>{part}</div>)}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground/60">No knowledge document yet. It will auto-populate when you run an agent session, or you can fill it in manually using Edit.</p>
              )}
            </div>
          )}

          {/* Stated Preferences */}
          <div className="pt-4 border-t border-border/50 space-y-3">
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Draper's Rules &amp; Preferences</div>
            <p className="text-xs text-muted-foreground/60">Rules Draper follows in every response. Add them here or by telling Draper in chat: "Remember that…", "Never…", "Always…"</p>

            {(knowledgeDoc?.statedPreferences || []).length === 0 ? (
              <p className="text-xs text-muted-foreground/50 italic">No rules set yet. Try telling Draper in chat: "Never use jargon in posts."</p>
            ) : (
              <ul className="space-y-2">
                {(knowledgeDoc?.statedPreferences || []).map((p: any, i: any) => (
                  <li key={i} className="flex items-start justify-between gap-2 bg-muted/20 rounded-xl px-3 py-2" data-testid={`pref-item-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground break-words">{p.rule}</p>
                      <p className="text-[10px] text-muted-foreground/50 mt-0.5">{p.source} · {new Date(p.addedAt).toLocaleDateString()}</p>
                    </div>
                    <button
                      data-testid={`button-delete-pref-${i}`}
                      disabled={isViewer}
                      title={isViewer ? "Editor or Owner access required" : "Remove this rule"}
                      onClick={async () => {
                        if (!activeWorkspaceId) return;
                        try {
                          await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/knowledge/preferences/${i}`);
                          await kdRefetch();
                        } catch { toast({ title: "Failed to remove rule", variant: "destructive" }); }
                      }}
                      className="text-muted-foreground/40 hover:text-red-400 transition-colors disabled:pointer-events-none disabled:opacity-30 flex-shrink-0 mt-0.5"
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {!isViewer && (
              <div className="flex gap-2 items-center">
                <input
                  data-testid="input-new-preference"
                  type="text"
                  placeholder='e.g. "Never use exclamation marks"'
                  value={kdNewPreference}
                  onChange={(e) => setKdNewPreference(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && kdNewPreference.trim() && activeWorkspaceId) {
                      setKdAddingPref(true);
                      try {
                        await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/knowledge/preferences`, { rule: kdNewPreference.trim() });
                        setKdNewPreference("");
                        await kdRefetch();
                      } catch { toast({ title: "Failed to add rule", variant: "destructive" }); }
                      finally { setKdAddingPref(false); }
                    }
                  }}
                  className="flex-1 bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:border-blue-500 outline-none text-foreground"
                />
                <button
                  data-testid="button-add-preference"
                  disabled={!kdNewPreference.trim() || kdAddingPref}
                  onClick={async () => {
                    if (!activeWorkspaceId || !kdNewPreference.trim()) return;
                    setKdAddingPref(true);
                    try {
                      await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/knowledge/preferences`, { rule: kdNewPreference.trim() });
                      setKdNewPreference("");
                      await kdRefetch();
                    } catch { toast({ title: "Failed to add rule", variant: "destructive" }); }
                    finally { setKdAddingPref(false); }
                  }}
                  className="px-3 py-2 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                >
                  {kdAddingPref ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                </button>
              </div>
            )}
          </div>
        </div>

        </TabsContent>

        <TabsContent value="notifications" className="space-y-6 mt-6">
        {/* Urgent alerts (P0): where they go, the owner-email fallback, and the open ones */}
        {activeWorkspaceId && usesClaudeTeam(getAiEngine(workspace)) && (
          <EscalationSettings workspaceId={activeWorkspaceId} telegram={telegramConfig} telegramLoading={telegramLoading} />
        )}

        {/* Notification Preferences */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-notification-preferences">
          <div className="flex items-center gap-3 mb-2">
            <Zap size={20} className="text-amber-400" />
            <h2 className="font-display font-bold text-foreground">Notification Preferences</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Choose which events trigger email alerts to your account email address.</p>
          <div className="space-y-3">
            {[
              { key: "agentRunComplete" as const, label: "Agent Run Complete", desc: "Get notified when an agent finishes a batch run." },
              { key: "hotLeadAlert" as const, label: "Hot Lead Alert", desc: "Instant alert when a lead crosses the high-priority threshold." },
              { key: "agentWaitingForInput" as const, label: "Agent Waiting for Input", desc: "When an agent is blocked and needs your decision." },
              { key: "campaignLaunchFailure" as const, label: "Campaign Launch Failure", desc: "Immediate alert if a Meta campaign fails to launch." },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-start justify-between gap-3 p-3 rounded-xl border border-border bg-muted/20">
                <div>
                  <p className="text-sm font-medium text-foreground">{label}</p>
                  <p className="text-[11px] text-muted-foreground/70 mt-0.5">{desc}</p>
                </div>
                <button
                  data-testid={`toggle-notif-${key}`}
                  onClick={() => setNotifPrefs(p => ({ ...p, [key]: !p[key] }))}
                  className={`shrink-0 w-10 h-5.5 rounded-full border transition-all flex items-center px-0.5 ${notifPrefs[key] ? "bg-blue-600 border-blue-500" : "bg-muted/50 border-border"}`}
                >
                  <span className={`w-4 h-4 rounded-full bg-white transition-transform ${notifPrefs[key] ? "translate-x-4" : "translate-x-0"}`} />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/20">
              <p className="text-sm font-medium text-foreground flex-1">Email Digest Frequency</p>
              <select
                data-testid="select-digest-frequency"
                value={notifPrefs.emailDigestFrequency}
                onChange={(e) => setNotifPrefs(p => ({ ...p, emailDigestFrequency: e.target.value as any }))}
                className="text-xs bg-muted/50 border border-border rounded-lg px-3 py-1.5 text-foreground"
              >
                <option value="instant">Instant</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Digest</option>
                <option value="never">Never</option>
              </select>
            </div>
          </div>
          <button
            data-testid="button-save-notification-prefs"
            onClick={async () => {
              if (!activeWorkspaceId) return;
              setSavingNotifPrefs(true);
              try {
                await apiRequest("PATCH", `/api/workspaces/${activeWorkspaceId}/notification-preferences`, notifPrefs);
                toast({ title: "Notification preferences saved" });
              } catch { toast({ title: "Save failed", variant: "destructive" }); }
              finally { setSavingNotifPrefs(false); }
            }}
            disabled={savingNotifPrefs || isViewer}
            title={isViewer ? "You need Editor or Owner access." : undefined}
            className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          >
            {savingNotifPrefs ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Notification Preferences
          </button>
        </div>

        {/* Telegram CMP Section */}
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-telegram-cmp">
          <div className="flex items-center gap-3 mb-2">
            <Bot size={20} className="text-blue-400" />
            <h2 className="font-display font-bold text-foreground">Draper via Telegram</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Connect Draper to Telegram so you can manage your marketing by text — approve posts, check leads, get morning briefs, and more. Create a bot via @BotFather, paste the token below, and send /start to link your account.</p>

          {telegramLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>
          ) : telegramConfig?.status === "connected" ? (
            <div className="space-y-5">
              {/* Connected state */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-950/30 border border-emerald-500/30">
                <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-emerald-300">Connected — @{telegramConfig.botUsername}</p>
                  {telegramConfig.linkedAt && <p className="text-[11px] text-muted-foreground mt-0.5">Linked {new Date(telegramConfig.linkedAt).toLocaleDateString()}</p>}
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="button-telegram-test"
                    onClick={async () => {
                      try {
                        await apiRequest("POST", `/api/telegram/test/${activeWorkspaceId}`, {});
                        toast({ title: "Test message sent to Telegram!" });
                      } catch { toast({ title: "Test failed — check your connection", variant: "destructive" }); }
                    }}
                    className="text-xs px-3 py-1.5 bg-muted/40 border border-border rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                  >Test</button>
                  <button
                    data-testid="button-telegram-disconnect"
                    onClick={async () => {
                      if (!confirm("Disconnect Draper from Telegram?")) return;
                      await apiRequest("DELETE", `/api/telegram/config/${activeWorkspaceId}`, {});
                      setTelegramConfig(null);
                      toast({ title: "Telegram disconnected" });
                    }}
                    className="text-xs px-3 py-1.5 bg-red-950/30 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-950/50 transition-colors"
                  >Disconnect</button>
                </div>
              </div>

              {/* Alert toggles */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alert Settings</p>
                {[
                  { key: "morningBrief" as const, label: "Morning Brief (8am)", desc: "Daily summary of posts, leads, and priorities" },
                  { key: "hotLeads" as const, label: "Hot Lead Alerts", desc: "Instant ping when a lead scores 75+" },
                  { key: "contentApprovals" as const, label: "Content Approval Requests", desc: "When Peggy creates drafts for your review" },
                  { key: "publishFailures" as const, label: "Publish Failures", desc: "If a scheduled post fails to go live" },
                  { key: "campaignAlerts" as const, label: "Campaign Alerts", desc: "Budget warnings and underperforming campaigns" },
                  { key: "strategyGaps" as const, label: "Calendar Gaps", desc: "When there's nothing scheduled for 3+ days" },
                  { key: "draperQuestions" as const, label: "Draper Strategic Questions", desc: "Proactive recommendations and questions from Draper" },
                  { key: "harryIntel" as const, label: "Harry Intel Finds", desc: "High-relevance market intel from Harry" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-start justify-between gap-3 p-3 rounded-xl border border-border bg-muted/20">
                    <div>
                      <p className="text-sm font-medium text-foreground">{label}</p>
                      <p className="text-[11px] text-muted-foreground/70 mt-0.5">{desc}</p>
                    </div>
                    <button
                      data-testid={`toggle-telegram-${key}`}
                      onClick={() => setTelegramAlerts(a => ({ ...a, [key]: !a[key] }))}
                      className={`shrink-0 w-10 h-5.5 rounded-full border transition-all flex items-center px-0.5 ${telegramAlerts[key] ? "bg-blue-600 border-blue-500" : "bg-muted/50 border-border"}`}
                    >
                      <span className={`w-4 h-4 rounded-full bg-white transition-transform ${telegramAlerts[key] ? "translate-x-4" : "translate-x-0"}`} />
                    </button>
                  </div>
                ))}
              </div>

              {/* Quiet hours */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Quiet Hours</p>
                <p className="text-[11px] text-muted-foreground/70">No proactive messages will be sent during these hours.</p>
                <div className="flex items-center gap-3 p-3 rounded-xl border border-border bg-muted/20">
                  <div className="flex-1 space-y-1">
                    <label className="text-[11px] text-muted-foreground">Start (no messages after)</label>
                    <select
                      data-testid="select-quiet-hours-start"
                      value={telegramQuietStart}
                      onChange={e => setTelegramQuietStart(Number(e.target.value))}
                      className="w-full bg-muted/30 border border-border rounded-lg px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i === 0 ? "12:00 AM" : i < 12 ? `${i}:00 AM` : i === 12 ? "12:00 PM" : `${i - 12}:00 PM`}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-1 space-y-1">
                    <label className="text-[11px] text-muted-foreground">End (messages resume after)</label>
                    <select
                      data-testid="select-quiet-hours-end"
                      value={telegramQuietEnd}
                      onChange={e => setTelegramQuietEnd(Number(e.target.value))}
                      className="w-full bg-muted/30 border border-border rounded-lg px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>{i === 0 ? "12:00 AM" : i < 12 ? `${i}:00 AM` : i === 12 ? "12:00 PM" : `${i - 12}:00 PM`}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <button
                data-testid="button-save-telegram-alerts"
                disabled={telegramSaving || isViewer}
                onClick={async () => {
                  setTelegramSaving(true);
                  try {
                    await apiRequest("PATCH", `/api/telegram/config/${activeWorkspaceId}`, {
                      alertsConfig: telegramAlerts,
                      quietHoursStart: telegramQuietStart,
                      quietHoursEnd: telegramQuietEnd,
                    });
                    toast({ title: "Alert settings saved" });
                  } catch { toast({ title: "Save failed", variant: "destructive" }); }
                  finally { setTelegramSaving(false); }
                }}
                className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
              >
                {telegramSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Alert Settings
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Setup state */}
              {telegramConfig?.status === "waiting_for_start" && (
                <div className="space-y-2 p-3 rounded-xl bg-amber-950/30 border border-amber-500/30">
                  <div className="flex items-center gap-3">
                    <Clock size={16} className="text-amber-400 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-300">Waiting for you to link</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">Tap the button below to open Telegram and complete the link. The link is one-time use and ties your Telegram identity to this workspace.</p>
                    </div>
                  </div>
                  {telegramConfig.botUsername && telegramConfig.linkCode && (
                    <a
                      href={`https://t.me/${telegramConfig.botUsername}?start=${telegramConfig.linkCode}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="link-telegram-start"
                      className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-medium transition-colors w-full"
                    >
                      Open Telegram to link
                    </a>
                  )}
                  {telegramConfig.botUsername && !telegramConfig.linkCode && (
                    <p className="text-[11px] text-muted-foreground">Re-save your bot token to generate a fresh link code.</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-muted-foreground">Step 1 — Create a bot</p>
                <div className="p-3 rounded-xl bg-muted/20 border border-border text-[12px] text-muted-foreground space-y-1">
                  <p>1. Open <span className="text-blue-400 font-medium">@BotFather</span> on Telegram</p>
                  <p>2. Send <span className="font-mono text-foreground">/newbot</span></p>
                  <p>3. Follow the prompts to name your bot</p>
                  <p>4. Copy the token BotFather gives you</p>
                </div>
                <p className="text-xs font-semibold text-muted-foreground mt-3">Step 2 — Paste your token</p>
                <div className="flex gap-2">
                  <input
                    data-testid="input-telegram-bot-token"
                    type="password"
                    placeholder="1234567890:AABBCCDDEEFFaabbccddeeff"
                    value={telegramBotToken}
                    onChange={e => setTelegramBotToken(e.target.value)}
                    className="flex-1 bg-muted/30 border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    data-testid="button-telegram-connect"
                    disabled={!telegramBotToken.trim() || telegramSaving || isViewer}
                    onClick={async () => {
                      setTelegramSaving(true);
                      try {
                        const data = await apiRequest("POST", `/api/telegram/config`, {
                          workspaceId: activeWorkspaceId,
                          botToken: telegramBotToken.trim(),
                        });
                        const json = await data.json();
                        if (json.error) throw new Error(json.error);
                        setTelegramConfig({ ...json, status: json.chatId ? "connected" : "waiting_for_start" });
                        setTelegramBotToken("");
                        toast({ title: "Bot configured! Use the link below to link your Telegram account." });
                      } catch (err) {
                        toast({ title: err instanceof Error ? err.message : "Connection failed", variant: "destructive" });
                      } finally { setTelegramSaving(false); }
                    }}
                    className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  >
                    {telegramSaving ? <Loader2 size={14} className="animate-spin" /> : "Connect"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-lead-capture">
          <div className="flex items-center gap-3 mb-2">
            <Webhook size={20} className="text-orange-400" />
            <h2 className="font-display font-bold text-foreground">Lead Capture API</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Connect your website forms to Rogers. When someone fills out a form on your site, the lead goes straight into your Lead Desk with full attribution tracking.</p>

          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-muted/30 border border-border space-y-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Key size={14} className="text-orange-400" />
                  <span className="text-sm font-bold text-foreground/70">API Key</span>
                </div>
                {leadApiKey ? (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="button-copy-api-key"
                      className="border-border text-muted-foreground text-xs h-7"
                      onClick={() => {
                        navigator.clipboard.writeText(leadApiKey);
                        setCopiedSnippet("key");
                        setTimeout(() => setCopiedSnippet(null), 2000);
                      }}
                    >
                      {copiedSnippet === "key" ? <Check size={12} className="mr-1" /> : <Copy size={12} className="mr-1" />}
                      {copiedSnippet === "key" ? "Copied" : "Copy"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="button-regenerate-api-key"
                      className="border-border text-muted-foreground text-xs h-7"
                      onClick={async () => {
                        setGeneratingKey(true);
                        try {
                          const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/generate-api-key`);
                          const data = await res.json();
                          setLeadApiKey(data.apiKey);
                          toast({ title: "API key regenerated" });
                        } catch { toast({ title: "Failed", variant: "destructive" }); }
                        finally { setGeneratingKey(false); }
                      }}
                      disabled={generatingKey}
                    >
                      <RefreshCw size={12} className={`mr-1 ${generatingKey ? "animate-spin" : ""}`} />
                      Regenerate
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="button-revoke-api-key"
                      className="border-red-500/20 text-red-400/60 text-xs h-7"
                      onClick={async () => {
                        try {
                          await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/api-key`);
                          setLeadApiKey(null);
                          toast({ title: "API key revoked" });
                        } catch { toast({ title: "Failed", variant: "destructive" }); }
                      }}
                    >
                      <Trash2 size={12} className="mr-1" />
                      Revoke
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    data-testid="button-generate-api-key"
                    className="bg-orange-600 text-white text-xs h-7"
                    onClick={async () => {
                      setGeneratingKey(true);
                      try {
                        const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/generate-api-key`);
                        const data = await res.json();
                        setLeadApiKey(data.apiKey);
                        toast({ title: "API key generated! Use it to connect your website forms." });
                      } catch { toast({ title: "Failed to generate", variant: "destructive" }); }
                      finally { setGeneratingKey(false); }
                    }}
                    disabled={generatingKey || isViewer}
                    title={isViewer ? "You need Editor or Owner access to manage API keys." : undefined}
                  >
                    {generatingKey ? <Loader2 size={12} className="animate-spin mr-1" /> : <Key size={12} className="mr-1" />}
                    Generate API Key
                  </Button>
                )}
              </div>
              {leadApiKey && (
                <div className="font-mono text-xs text-orange-400/80 bg-background/30 px-3 py-2 rounded-lg break-all border border-orange-500/10" data-testid="text-api-key">
                  {leadApiKey}
                </div>
              )}
            </div>

            {leadApiKey && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  {(["html", "curl", "zapier"] as const).map(type => (
                    <button
                      key={type}
                      data-testid={`tab-snippet-${type}`}
                      onClick={() => setShowSnippetType(type)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${showSnippetType === type ? "bg-orange-600/20 text-orange-400 border border-orange-500/30" : "bg-muted/50 text-muted-foreground border border-border"}`}
                    >
                      {type === "html" ? "HTML Form" : type === "curl" ? "API (cURL)" : "Zapier / Webhook"}
                    </button>
                  ))}
                </div>

                <div className="relative">
                  <pre className="text-[11px] text-muted-foreground bg-background/40 rounded-xl p-4 overflow-x-auto border border-border whitespace-pre-wrap" data-testid="text-snippet">
                    {(() => {
                      const appUrl = window.location.origin;
                      if (showSnippetType === "html") return `<!-- Paste this into your website -->
<form id="drpr-lead-form" onsubmit="captureLeadDRPR(event)">
  <input name="name" placeholder="Your Name" required />
  <input name="email" type="email" placeholder="Email" required />
  <input name="phone" placeholder="Phone (optional)" />
  <input name="interests" placeholder="Service interested in" />
  <button type="submit">Get Started</button>
</form>

<script>
async function captureLeadDRPR(e) {
  e.preventDefault();
  var f = e.target;
  var params = new URLSearchParams(window.location.search);
  var res = await fetch("${appUrl}/api/public/lead-capture", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": "${leadApiKey}"
    },
    body: JSON.stringify({
      name: f.name.value,
      email: f.email.value,
      phone: f.phone.value,
      source: "Website Form",
      interests: [f.interests.value].filter(Boolean),
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      utm_content: params.get("utm_content"),
      page_slug: window.location.pathname
    })
  });
  if (res.ok) {
    f.reset();
    alert("Thank you! We'll be in touch.");
  }
}
</script>`;
                      if (showSnippetType === "curl") return `# Send a lead via API
curl -X POST ${appUrl}/api/public/lead-capture \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${leadApiKey}" \\
  -d '{
    "name": "Jane Smith",
    "email": "jane@example.com",
    "phone": "+1234567890",
    "source": "Website",
    "interests": ["Marketing Audit"],
    "preferredChannel": "email",
    "sequenceType": "welcome",
    "utm_source": "google",
    "utm_campaign": "spring-promo",
    "customFields": {
      "company": "Acme Corp",
      "budget": "$5k-10k"
    }
  }'`;
                      return `Webhook URL:
${appUrl}/api/public/lead-capture

Method: POST
Headers:
  Content-Type: application/json
  X-API-Key: ${leadApiKey}

Body fields:
  name (required) — Lead's full name
  email (required) — Lead's email
  phone — Phone number
  source — Where the lead came from
  interests — Array of services/products
  preferredChannel — "email" or "sms"
  sequenceType — "welcome", "sales", "educational", or "re-engagement"
  utm_source, utm_medium, utm_campaign, utm_content — Attribution
  customFields — Object with any custom key-value pairs
  page_slug — Page path the form was on`;
                    })()}
                  </pre>
                  <button
                    data-testid="button-copy-snippet"
                    onClick={() => {
                      const el = document.querySelector('[data-testid="text-snippet"]');
                      if (el) {
                        navigator.clipboard.writeText(el.textContent || "");
                        setCopiedSnippet("snippet");
                        setTimeout(() => setCopiedSnippet(null), 2000);
                      }
                    }}
                    className="absolute top-2 right-2 p-1.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                  >
                    {copiedSnippet === "snippet" ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-muted-foreground/70" />}
                  </button>
                </div>

                <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
                  <p className="text-[11px] text-blue-400/70 leading-relaxed">
                    <strong>How it works:</strong> When someone fills out your form, the lead appears in your Lead Desk instantly. Rogers will automatically send a personalized welcome email based on your strategy, then nurture them on a schedule. All UTM tracking and ad attribution is captured automatically.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
        </TabsContent>

        <TabsContent value="memory" className="space-y-6 mt-6">
          <Suspense fallback={<Loader2 className="animate-spin" />}><MemoryTab workspaceId={activeWorkspaceId} /></Suspense>
        </TabsContent>

        <TabsContent value="routines" className="space-y-6 mt-6">
          <Suspense fallback={<Loader2 className="animate-spin" />}><RoutinesTab workspaceId={activeWorkspaceId} /></Suspense>
        </TabsContent>

        <TabsContent value="content-targeting" className="space-y-6 mt-6">
          <Suspense fallback={<Loader2 className="animate-spin" />}><ContentTargetingTab workspaceId={activeWorkspaceId} /></Suspense>
        </TabsContent>

        <TabsContent value="personal-brand" className="space-y-6 mt-6">
          <Suspense fallback={<Loader2 className="animate-spin" />}><PersonalBrandTab workspaceId={activeWorkspaceId} /></Suspense>
        </TabsContent>

        <TabsContent value="site-agent" className="space-y-6 mt-6">
          <Suspense fallback={<Loader2 className="animate-spin" />}><SiteAgentTab workspaceId={activeWorkspaceId} /></Suspense>
        </TabsContent>
        <TabsContent value="agency-branding" className="space-y-6 mt-6">
          <Suspense fallback={<Loader2 className="animate-spin" />}><AgencyBrandingTab workspaceId={activeWorkspaceId} /></Suspense>
        </TabsContent>
      </Tabs>

      <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <Share2 size={20} className="text-blue-400" />
          <h2 className="font-display font-bold text-foreground">Social Media Profiles</h2>
        </div>
        <p className="text-xs text-muted-foreground/70">Your social profile URLs help Peggy analyze your existing brand presence and create consistent content.</p>
        <div className="space-y-3">
          {socialFields.map(({ key, label, icon: Icon, placeholder }) => (
            <div key={key} className="flex items-center gap-3">
              <Icon className="text-muted-foreground/50 shrink-0" size={16} />
              <input
                data-testid={`input-social-${key}`}
                value={form.socialUrls[key] || ""}
                onChange={(e) => setForm({ ...form, socialUrls: { ...form.socialUrls, [key]: e.target.value } })}
                placeholder={placeholder}
                className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
        <div className="flex items-center gap-3 mb-2">
          <Users size={20} className="text-blue-400" />
          <h2 className="font-display font-bold text-foreground">Audience & Voice</h2>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Target Audience</label>
          <textarea
            data-testid="input-target-audience"
            value={form.targetAudience}
            onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
            placeholder="Describe your ideal customer..."
            rows={3}
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand Voice</label>
          <div className="flex items-center gap-2">
            <Palette size={16} className="text-muted-foreground/50" />
            <select
              data-testid="select-brand-voice"
              value={form.brandVoice}
              onChange={(e) => setForm({ ...form, brandVoice: e.target.value })}
              className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground appearance-none"
            >
              <option value="Professional">Professional</option>
              <option value="Casual">Casual</option>
              <option value="Bold">Bold</option>
              <option value="Friendly">Friendly</option>
              <option value="Technical">Technical</option>
              <option value="Luxury">Luxury</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Brand Tone Description</label>
          <textarea
            data-testid="input-brand-tone"
            value={form.brandTone}
            onChange={(e) => setForm({ ...form, brandTone: e.target.value })}
            placeholder="Describe your brand's personality and communication style in detail..."
            rows={3}
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none placeholder:text-muted-foreground/50"
          />
        </div>
      </div>

      {/* Ad Budget Envelopes */}
      <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="settings-ad-budget-section">
        <div className="flex items-center gap-3 mb-2">
          <TrendingUp size={20} className="text-emerald-400" />
          <h2 className="font-display font-bold text-foreground">Ad Budget</h2>
        </div>
        <p className="text-xs text-muted-foreground/70">Set Pete's weekly spending envelope per ad platform. Pete will autonomously launch approved campaign drafts within the limit, and pause campaigns when the envelope is exhausted. Resets every Monday.</p>
        <div className="space-y-4">
          {(["meta", "google", "linkedin", "x"] as const).map((platform) => {
            const env = budgetEnvelopes.find((e: any) => e.platform === platform);
            const limit = env?.weeklyLimitCents ?? 0;
            const spent = env?.currentWeekSpentCents ?? 0;
            const remaining = Math.max(limit - spent, 0);
            const pct = limit > 0 ? Math.min((spent / limit) * 100, 100) : 0;
            const barColor = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
            const platformLabel = { meta: "Meta Ads", google: "Google Ads", linkedin: "LinkedIn Ads", x: "X / Twitter Ads" }[platform];
            const inputVal = envelopeInputs[platform] ?? (limit > 0 ? (limit / 100).toFixed(2) : "");
            return (
              <div key={platform} className="space-y-2.5" data-testid={`settings-envelope-row-${platform}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-foreground">{platformLabel}</p>
                    {limit > 0 && (
                      <p className="text-[11px] text-muted-foreground/60 mt-0.5">
                        ${(spent / 100).toFixed(2)} spent · ${(remaining / 100).toFixed(2)} remaining
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="flex items-center gap-1">
                      <span className="text-muted-foreground/50 text-sm">$</span>
                      <input
                        type="number"
                        min="0"
                        step="10"
                        placeholder="No limit"
                        value={inputVal}
                        onChange={(e) => setEnvelopeInputs((prev) => ({ ...prev, [platform]: e.target.value }))}
                        className="w-24 bg-muted/50 border border-border rounded-xl px-3 py-2 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/40 text-right"
                        data-testid={`settings-input-envelope-${platform}`}
                      />
                      <span className="text-[11px] text-muted-foreground/50">/wk</span>
                    </div>
                    <button
                      onClick={() => {
                        const rawVal = envelopeInputs[platform] ?? "";
                        const cents = rawVal === "" ? 0 : Math.round(parseFloat(rawVal) * 100);
                        if (!isNaN(cents) && cents >= 0) {
                          upsertEnvelope.mutate({ platform, weeklyLimitCents: cents });
                        }
                      }}
                      disabled={upsertEnvelope.isPending || isViewer}
                      title={isViewer ? "You need Editor or Owner access." : undefined}
                      className="bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl px-3 py-2 text-sm font-medium hover:bg-blue-600/30 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                      data-testid={`settings-button-save-envelope-${platform}`}
                    >
                      {upsertEnvelope.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                      Save
                    </button>
                  </div>
                </div>
                {limit > 0 && (
                  <div className="h-1 rounded-full bg-muted/50 overflow-hidden">
                    <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-start gap-2.5 px-4 py-3 bg-emerald-500/5 border border-emerald-500/15 rounded-xl">
          <Bot size={14} className="text-emerald-400 mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground/70">Pete runs a daily budget check and will automatically launch draft campaigns that fit within your envelope. Set a limit of $0 to disable Pete for that platform.</p>
        </div>
      </div>

      <span title={isViewer ? "You need Editor or Owner access." : undefined} className={isViewer ? "cursor-not-allowed" : ""}>
        <button
          data-testid="button-save-settings"
          onClick={() => updateMutation.mutate(form)}
          disabled={updateMutation.isPending || isViewer}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:pointer-events-none"
        >
          {updateMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          <span>Save Settings</span>
        </button>
      </span>

      {metaWebhookInfo && (
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="section-meta-webhook">
            <div className="flex items-center gap-3 mb-1">
              <Webhook size={20} className="text-[#1877F2]" />
              <h2 className="font-display font-bold text-foreground">Meta Lead Webhook</h2>
            </div>
            <p className="text-xs text-muted-foreground/70">Connect your Meta (Facebook/Instagram) Ads account to receive leads in real-time the moment someone fills out a lead form — no polling delay.</p>
            <div className="space-y-3">
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase font-semibold">Webhook URL</span>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] font-mono bg-muted/50 border border-border rounded-lg px-3 py-2 text-foreground/70 truncate" data-testid="text-meta-webhook-url">{metaWebhookInfo.webhookUrl}</code>
                  <button
                    data-testid="button-copy-webhook-url"
                    onClick={() => { navigator.clipboard.writeText(metaWebhookInfo.webhookUrl); setCopiedWebhook("url"); setTimeout(() => setCopiedWebhook(null), 2000); }}
                    className="p-2 rounded-lg bg-muted/50 hover:bg-muted border border-border transition-colors"
                  >
                    {copiedWebhook === "url" ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-muted-foreground/70" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-muted-foreground uppercase font-semibold">Verify Token</span>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[11px] font-mono bg-muted/50 border border-border rounded-lg px-3 py-2 text-foreground/70 truncate" data-testid="text-meta-webhook-verify-token">{metaWebhookInfo.verifyToken}</code>
                  <button
                    data-testid="button-copy-verify-token"
                    onClick={() => { navigator.clipboard.writeText(metaWebhookInfo.verifyToken); setCopiedWebhook("token"); setTimeout(() => setCopiedWebhook(null), 2000); }}
                    className="p-2 rounded-lg bg-muted/50 hover:bg-muted border border-border transition-colors"
                  >
                    {copiedWebhook === "token" ? <Check size={14} className="text-green-400" /> : <Copy size={14} className="text-muted-foreground/70" />}
                  </button>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/10">
                <p className="text-[11px] text-blue-400/70 leading-relaxed">
                  <strong>Setup:</strong> In Meta Business Suite → Events Manager → Webhooks, paste the URL above and set the Verify Token. Subscribe to the <code>leadgen</code> field. New leads will appear in your Lead Desk instantly and Rogers will begin nurturing them automatically.
                </p>
              </div>
            </div>
          </div>
        )}

        {!pilotMode && <SubscriptionSection workspace={workspace} />}

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-6" data-testid="section-api-access">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-orange-400" />
              <h2 className="font-display font-bold text-foreground">API Access</h2>
            </div>
            {(hasApiKey || !!externalApiKey) && (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={testApiConnection}
                  disabled={testingConnection || !externalApiKey}
                  data-testid="button-test-connection"
                >
                  {testingConnection ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />}
                  Test Connection
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-400 border-red-500/20"
                  data-testid="button-revoke-external-key"
                  onClick={async () => {
                    try {
                      await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/external-api-key`);
                      setExternalApiKey(null);
                      setHasApiKey(false);
                      setKeyPrefix(null);
                      toast({ title: "API Key Revoked" });
                    } catch { toast({ title: "Failed to revoke key", variant: "destructive" }); }
                  }}
                >
                  <Trash2 size={12} className="mr-1" />
                  Revoke
                </Button>
              </div>
            )}
          </div>

          {!(hasApiKey || !!externalApiKey) ? (
            <div className="flex flex-col items-center justify-center py-8 border border-dashed border-border rounded-xl bg-muted/20 space-y-4">
              <div className="p-3 rounded-full bg-orange-500/10">
                <Bot size={32} className="text-orange-400" />
              </div>
              <div className="text-center px-6">
                <p className="font-bold text-foreground">External Agent API</p>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                  Generate an API key to connect your workspace to external agents like Claude Desktop, n8n, or your own custom tools via our MCP server.
                </p>
              </div>
              <Button
                data-testid="button-generate-external-key"
                onClick={async () => {
                  setGeneratingExternalKey(true);
                  try {
                    const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/external-api-key`);
                    const data = await res.json();
                    setExternalApiKey(data.apiKey);
                    setHasApiKey(true);
                    setKeyPrefix(data.apiKey ? data.apiKey.substring(0, 12) : null);
                    toast({ title: "API Key Generated" });
                  } catch { toast({ title: "Failed to generate key", variant: "destructive" }); }
                  finally { setGeneratingExternalKey(false); }
                }}
                disabled={generatingExternalKey || isViewer}
                title={isViewer ? "You need Editor or Owner access to manage API keys." : undefined}
                className="bg-orange-600 hover:bg-orange-500 disabled:pointer-events-none"
              >
                {generatingExternalKey ? <Loader2 size={14} className="animate-spin mr-2" /> : <Key size={14} className="mr-2" />}
                Generate API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="bg-background/50 border border-border rounded-xl p-4 flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-1">Your API Key</p>
                  <p className="font-mono text-sm text-orange-400 truncate" data-testid="text-external-api-key">
                    {(externalApiKey ?? keyPrefix ?? "drpr_").substring(0, 12)}••••••••••••••••
                  </p>
                  {!externalApiKey && <p className="text-[10px] text-muted-foreground mt-0.5">Key is stored securely — regenerate to reveal a new one</p>}
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => {
                    if (externalApiKey) {
                      navigator.clipboard.writeText(externalApiKey);
                      setCopiedSnippet("external-key");
                      setTimeout(() => setCopiedSnippet(null), 2000);
                    }
                  }}
                  disabled={!externalApiKey}
                  data-testid="button-copy-external-key"
                >
                  {copiedSnippet === "external-key" ? <CheckCircle2 size={16} className="text-green-400" /> : <Copy size={16} className="text-muted-foreground" />}
                </Button>
              </div>

              {/* API Usage Stats */}
              {apiUsageStats && (
                <div className="grid grid-cols-3 gap-3" data-testid="section-api-usage-stats">
                  <div className="bg-background/50 border border-border rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-foreground" data-testid="stat-api-calls-24h">{apiUsageStats.total24h}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">Calls (24h)</p>
                  </div>
                  <div className="bg-background/50 border border-border rounded-xl p-3 text-center">
                    <p className="text-lg font-bold text-foreground" data-testid="stat-api-calls-7d">{apiUsageStats.total7d}</p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">Calls (7d)</p>
                  </div>
                  <div className="bg-background/50 border border-border rounded-xl p-3 text-center">
                    <p className="text-xs font-semibold text-foreground truncate" data-testid="stat-api-last-used">
                      {apiUsageStats.lastUsedAt ? new Date(apiUsageStats.lastUsedAt).toLocaleDateString() : "Never"}
                    </p>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-widest mt-0.5">Last Used</p>
                  </div>
                  {apiUsageStats.topActions.length > 0 && (
                    <div className="col-span-3 bg-background/50 border border-border rounded-xl p-3 space-y-2">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">Top Endpoints (7d)</p>
                      <div className="space-y-1.5">
                        {apiUsageStats.topActions.map(({ action, count }) => (
                          <div key={action} className="flex items-center justify-between gap-2">
                            <code className="text-[11px] text-orange-400 truncate">{action}</code>
                            <span className="text-[11px] text-muted-foreground shrink-0">{count}×</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bot size={18} className="text-blue-400" />
                    <h3 className="font-display font-bold text-foreground">Connect Your Agent</h3>
                  </div>
                  <a href="/api-docs" className="text-xs text-orange-400 hover:underline flex items-center gap-1" data-testid="link-full-developer-docs">
                    <ExternalLink size={11} /> Full Docs
                  </a>
                </div>

                <div className="flex border-b border-border">
                  {(["claude", "n8n", "rest"] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveSetupTab(tab)}
                      className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-[2px] ${
                        activeSetupTab === tab
                          ? "border-orange-500 text-foreground"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`tab-setup-${tab}`}
                    >
                      {tab === "claude" ? "Claude Desktop" : tab === "n8n" ? "n8n" : "Custom/REST"}
                    </button>
                  ))}
                </div>

                <div className="bg-muted/30 border border-border rounded-xl p-4">
                  {activeSetupTab === "claude" && (
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">Add drpr.ai to your Claude Desktop configuration file (<code>~/Library/Application Support/Claude/claude_desktop_config.json</code>):</p>
                      <pre className="text-[11px] font-mono bg-background/50 p-3 rounded-lg border border-border overflow-x-auto">
{`{
  "mcpServers": {
    "drpr": {
      "command": "npx",
      "args": ["@modelcontextprotocol/server-http"],
      "env": {
        "URL": "${window.location.origin}/mcp/v1",
        "X_API_KEY": "${externalApiKey || "YOUR_API_KEY"}"
      }
    }
  }
}`}
                      </pre>
                    </div>
                  )}

                  {activeSetupTab === "n8n" && (
                    <div className="space-y-3">
                      <p className="text-xs text-muted-foreground">Use the <strong>HTTP Request</strong> node with these settings:</p>
                      <ul className="text-xs space-y-1 text-muted-foreground list-disc list-inside">
                        <li><strong>Method:</strong> POST</li>
                        <li><strong>URL:</strong> <code>${window.location.origin}/api/v1/leads</code></li>
                        <li><strong>Header:</strong> <code>X-API-Key: ${externalApiKey || "YOUR_API_KEY"}</code></li>
                      </ul>
                      <pre className="text-[11px] font-mono bg-background/50 p-3 rounded-lg border border-border overflow-x-auto">
{`{
  "parameters": {
    "method": "POST",
    "url": "${window.location.origin}/api/v1/leads",
    "authentication": "genericCredentialType",
    "genericAuthType": "httpHeaderAuth",
    "headerParameters": {
      "parameters": [
        { "name": "X-API-Key", "value": "${externalApiKey || "YOUR_API_KEY"}" }
      ]
    },
    "sendBody": true,
    "bodyParameters": {
      "parameters": [
        { "name": "name", "value": "={{ $json.name }}" },
        { "name": "email", "value": "={{ $json.email }}" }
      ]
    }
  }
}`}
                      </pre>
                    </div>
                  )}

                  {activeSetupTab === "rest" && (
                    <div className="space-y-4">
                      <p className="text-xs text-muted-foreground">Example cURL to fetch workspace info:</p>
                      <pre className="text-[11px] font-mono bg-background/50 p-3 rounded-lg border border-border overflow-x-auto">
{`curl -X GET "${window.location.origin}/api/v1/workspace" \\
  -H "X-API-Key: ${externalApiKey || "YOUR_API_KEY"}"`}
                      </pre>
                      <p className="text-xs text-muted-foreground">Example cURL to create a post:</p>
                      <pre className="text-[11px] font-mono bg-background/50 p-3 rounded-lg border border-border overflow-x-auto">
{`curl -X POST "${window.location.origin}/api/v1/posts" \\
  -H "X-API-Key: ${externalApiKey || "YOUR_API_KEY"}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "platform": "linkedin",
    "content": "Exciting news! We just launched our new API.",
    "status": "draft"
  }'`}
                      </pre>
                    </div>
                  )}
                </div>
              </div>

              <div className="border border-border rounded-xl overflow-hidden">
                <button
                  onClick={() => setApiRefOpen(!apiRefOpen)}
                  className="w-full flex items-center justify-between p-4 bg-muted/20 hover:bg-muted/30 transition-colors"
                  data-testid="button-toggle-api-ref"
                >
                  <div className="flex items-center gap-2">
                    <Code2 size={16} className="text-blue-400" />
                    <span className="text-sm font-bold">API Reference</span>
                    <a href="/api-docs" className="text-[10px] text-orange-400 hover:underline ml-2" data-testid="link-full-api-docs" onClick={e => e.stopPropagation()}>Full Docs</a>
                  </div>
                  <ChevronDown size={16} className={`text-muted-foreground transition-transform ${apiRefOpen ? "rotate-180" : ""}`} />
                </button>
                {apiRefOpen && (
                  <div className="p-4 bg-background/50 space-y-6 border-t border-border">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-[10px] font-bold">GET</span>
                          <code className="text-xs">/api/v1/workspace</code>
                        </div>
                        <p className="text-xs text-muted-foreground">Returns details about the current workspace.</p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-[10px] font-bold">GET</span>
                          <code className="text-xs">/api/v1/leads</code>
                        </div>
                        <p className="text-xs text-muted-foreground">Fetch recent leads (limit 50).</p>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 text-[10px] font-bold">POST</span>
                          <code className="text-xs">/api/v1/leads</code>
                        </div>
                        <p className="text-xs text-muted-foreground">Add a new lead to Lead Desk.</p>
                        <pre className="text-[10px] bg-muted/50 p-2 rounded-lg">
                          {`{ "name": "string", "email": "string", "phone": "string", "notes": "string" }`}
                        </pre>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 text-[10px] font-bold">POST</span>
                          <code className="text-xs">/api/v1/posts</code>
                        </div>
                        <p className="text-xs text-muted-foreground">Create a social media post draft or schedule it.</p>
                        <pre className="text-[10px] bg-muted/50 p-2 rounded-lg">
                          {`{ "platform": "linkedin|x|facebook", "content": "string", "status": "draft|scheduled" }`}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-webhooks">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Webhook size={20} className="text-orange-400" />
              <h2 className="font-display font-bold text-foreground">Webhooks</h2>
            </div>
            <a href="/api-docs#webhooks" className="text-xs text-orange-400 hover:underline flex items-center gap-1" data-testid="link-webhook-docs">
              <ExternalLink size={12} /> Docs
            </a>
          </div>
          <p className="text-xs text-muted-foreground">Receive real-time notifications when events happen in your workspace. Payloads are signed with HMAC-SHA256.</p>

          {webhooks.length > 0 && (
            <div className="space-y-2">
              {webhooks.map((wh: any) => (
                <div key={wh.id} className="flex items-center justify-between bg-background/50 border border-border rounded-xl p-3" data-testid={`webhook-item-${wh.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-mono text-foreground/80 truncate">{wh.url}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-muted-foreground">Events: {wh.events?.join(", ") || "*"}</span>
                      {wh.lastTriggeredAt && <span className="text-[10px] text-muted-foreground">Last: {new Date(wh.lastTriggeredAt).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="text-red-400 shrink-0"
                    data-testid={`button-delete-webhook-${wh.id}`}
                    onClick={async () => {
                      try {
                        await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/webhooks/${wh.id}`);
                        setWebhooks(prev => prev.filter((w: any) => w.id !== wh.id));
                        toast({ title: "Webhook removed" });
                      } catch { toast({ title: "Failed to remove webhook", variant: "destructive" }); }
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="url"
              placeholder="https://your-server.com/webhook"
              value={newWebhookUrl}
              onChange={e => setNewWebhookUrl(e.target.value)}
              className="flex-1 bg-background/50 border border-border rounded-xl px-3 py-2 text-sm font-mono text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
              data-testid="input-webhook-url"
            />
            <select
              value={newWebhookEvents}
              onChange={e => setNewWebhookEvents(e.target.value)}
              className="bg-background/50 border border-border rounded-xl px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-orange-500/50"
              data-testid="select-webhook-events"
            >
              <option value="*">All events</option>
              <option value="lead.created">lead.created</option>
              <option value="agent.completed">agent.completed</option>
              <option value="content.approved">content.approved</option>
            </select>
            <Button
              disabled={!newWebhookUrl || addingWebhook}
              className="bg-orange-600 hover:bg-orange-500"
              data-testid="button-add-webhook"
              onClick={async () => {
                setAddingWebhook(true);
                try {
                  const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/webhooks`, {
                    url: newWebhookUrl,
                    events: newWebhookEvents === "*" ? ["*"] : [newWebhookEvents],
                  });
                  const created = await res.json();
                  setWebhooks(prev => [...prev, created]);
                  setNewWebhookUrl("");
                  toast({ title: "Webhook registered", description: `Secret: ${created.secret?.substring(0, 16)}... (copy from response)` });
                } catch { toast({ title: "Failed to add webhook", variant: "destructive" }); }
                finally { setAddingWebhook(false); }
              }}
            >
              {addingWebhook ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
              Add
            </Button>
          </div>
        </div>

        <EmailOutboxSection workspaceId={activeWorkspaceId} />

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <DollarSign size={20} className="text-emerald-400" />
            <h2 className="font-display font-bold text-foreground">AI Usage & Billing</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Track your AI usage costs. Charges are based on actual AI consumption (text generation, image creation, etc.).</p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Usage Balance</span>
              <span className="text-xl font-bold text-emerald-400" data-testid="text-ai-balance">
                ${(aiUsage?.aiUsageBalance || 0).toFixed(4)}
              </span>
              <span className="text-[9px] text-muted-foreground/40 block mt-0.5">Accumulated AI cost (auto-charged at $10 if enabled)</span>
            </div>
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Total Spent</span>
              <span className="text-xl font-bold text-foreground" data-testid="text-ai-total-cost">
                ${(aiUsage?.summary?.totalCost || 0).toFixed(4)}
              </span>
            </div>
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">AI Calls</span>
              <span className="text-xl font-bold text-foreground" data-testid="text-ai-call-count">
                {aiUsage?.summary?.logCount || 0}
              </span>
            </div>
          </div>

          {(aiUsage?.aiUsageBalance || 0) >= 8 && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm">
              <AlertTriangle size={16} />
              <span>Approaching $10 billing threshold{overageStatus?.overageBillingEnabled ? ". You will be auto-charged when balance reaches $10." : ". Enable overage billing below to auto-charge instead of pausing."}</span>
            </div>
          )}

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Monthly Spending Limit</label>
            <div className="flex items-center gap-2">
              <DollarSign size={14} className="text-muted-foreground/50 shrink-0" />
              <input
                data-testid="input-spending-limit"
                type="number"
                step="1"
                value={spendingLimit}
                onChange={(e) => setSpendingLimit(e.target.value)}
                placeholder="No limit set"
                className="flex-1 bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
              />
              <button
                onClick={saveSpendingLimit}
                disabled={savingLimit || isViewer}
                title={isViewer ? "You need Editor or Owner access." : undefined}
                className="bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl px-4 py-3 text-sm font-medium hover:bg-blue-600/30 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                data-testid="button-save-spending-limit"
              >
                {savingLimit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground/50 mt-1.5">Leave empty for no limit. AI operations will pause when this limit is reached.</p>
          </div>

          {aiUsage?.logs && aiUsage.logs.length > 0 && (
            <div>
              <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Recent AI Activity</h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {aiUsage.logs.slice(0, 15).map((log: any) => (
                  <div key={log.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-muted/30 border border-border">
                    <div className="flex items-center gap-2 min-w-0">
                      <Zap size={12} className="text-blue-400 shrink-0" />
                      <span className="text-xs text-muted-foreground truncate">{log.service}</span>
                      <span className="text-[10px] text-muted-foreground/50 font-mono">{log.model}</span>
                    </div>
                    <span className="text-xs font-mono text-emerald-400 shrink-0">${(log.totalCost || 0).toFixed(6)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* AI Overage Billing (hidden in PILOT_MODE: no card / charges during the pilot) */}
        {!pilotMode && (
        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5">
          <div className="flex items-center gap-3 mb-2">
            <Zap size={20} className="text-yellow-400" />
            <h2 className="font-display font-bold text-foreground">AI Overage Billing</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">
            When enabled, AI usage is charged automatically at $10 intervals instead of pausing your account when you exceed your plan's included AI calls.
          </p>

          {overageStatus?.chargeFailed && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <AlertTriangle size={16} />
              <span>Your last overage charge failed. Please update your payment method to re-enable auto-charging.</span>
            </div>
          )}

          <div className="bg-muted/30 border border-border rounded-xl p-4 flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-foreground">Auto-Charge Overages</div>
              <div className="text-xs text-muted-foreground/70 mt-0.5">
                {overageStatus?.hasCard
                  ? overageStatus.overageBillingEnabled
                    ? "Active — AI overages will be charged at $10 intervals"
                    : "Card saved — billing is currently paused"
                  : "No payment method saved"}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {overageStatus?.overageBillingEnabled ? (
                <button
                  data-testid="button-disable-overage"
                  onClick={() => toggleOverageBilling(false)}
                  disabled={togglingOverage || isViewer}
                  className="text-xs px-4 py-2 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {togglingOverage ? <Loader2 size={13} className="animate-spin" /> : null}
                  Disable
                </button>
              ) : (
                <button
                  data-testid="button-enable-overage"
                  onClick={() => toggleOverageBilling(true)}
                  disabled={togglingOverage || settingUpCard || isViewer}
                  className="text-xs px-4 py-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {(togglingOverage || settingUpCard) ? <Loader2 size={13} className="animate-spin" /> : null}
                  {overageStatus?.hasCard ? "Enable" : "Add Card & Enable"}
                </button>
              )}
              {overageStatus?.hasCard && (
                <button
                  data-testid="button-update-overage-card"
                  onClick={() => {
                    setSettingUpCard(true);
                    fetch("/api/stripe/setup-payment-method", {
                      method: "POST",
                      credentials: "include",
                      headers: { "Content-Type": "application/json" },
                    })
                      .then(r => r.json())
                      .then(d => { if (d.url) window.location.href = d.url; else { toast({ title: "Could not open card setup", variant: "destructive" }); setSettingUpCard(false); } })
                      .catch(() => { toast({ title: "Error", variant: "destructive" }); setSettingUpCard(false); });
                  }}
                  disabled={settingUpCard || isViewer}
                  className="text-xs px-3 py-2 rounded-xl border border-border bg-muted/40 text-muted-foreground hover:bg-muted/70 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:pointer-events-none"
                >
                  {settingUpCard ? <Loader2 size={13} className="animate-spin" /> : null}
                  Update Card
                </button>
              )}
            </div>
          </div>

          <div className="text-[10px] text-muted-foreground/40 leading-relaxed">
            Overage charges are billed to your saved card when your accumulated AI balance exceeds $10. This resets the balance to $0. The charge is for actual AI model costs only and does not include Draper subscription fees.
          </div>
        </div>
        )}

      <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="team-management-section">
        <div className="flex items-center gap-3">
          <UserCog size={18} className="text-blue-400" />
          <h2 className="font-display font-bold text-foreground">Team Management</h2>
        </div>
        <TeamPage />
      </div>

      <div className="bg-red-950/20 border border-red-500/20 rounded-2xl p-4 md:p-6 space-y-4" data-testid="danger-zone">
        <div className="flex items-center gap-3">
          <AlertTriangle size={18} className="text-red-400" />
          <h2 className="font-display font-bold text-red-400">Danger Zone</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-red-400">Reset Workspace</h3>
            <p className="text-[11px] text-muted-foreground/70">Wipe all workspace data while keeping your subscription. This clears leads, campaigns, content, and agent configurations.</p>
            
            {!showResetConfirm ? (
              <span title={isViewer ? "You need Editor or Owner access to reset the workspace." : undefined} className={isViewer ? "cursor-not-allowed inline-block" : "inline-block"}>
                <button
                  data-testid="button-reset-workspace"
                  onClick={() => setShowResetConfirm(true)}
                  disabled={isViewer}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  <RefreshCw size={12} />
                  Reset Workspace
                </button>
              </span>
            ) : (
              <div className="space-y-3 p-3 bg-red-950/30 border border-red-500/25 rounded-lg">
                <p className="text-[11px] text-red-300">Type <span className="font-mono bg-red-500/15 px-1 py-0.5 rounded">RESET</span> to confirm:</p>
                <input
                  data-testid="input-reset-confirm"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  placeholder="RESET"
                  className="w-full bg-muted/50 border border-red-500/30 rounded-lg px-3 py-2 text-xs focus:border-red-500 outline-none text-foreground"
                />
                <div className="flex items-center gap-2">
                  <button
                    data-testid="button-confirm-reset"
                    onClick={handleResetWorkspace}
                    disabled={resetting || resetConfirmText !== "RESET"}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-red-600/80 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors disabled:opacity-40"
                  >
                    {resetting ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    Confirm Reset
                  </button>
                  <button
                    onClick={() => { setShowResetConfirm(false); setResetConfirmText(""); }}
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="bg-red-500/5 border border-red-500/10 rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-red-400">Delete Workspace</h3>
            <p className="text-[11px] text-muted-foreground/70">Permanently delete this workspace and all associated data. This action cannot be undone.</p>
            
            {!showDeleteConfirm ? (
              <span title={isViewer ? "You need Editor or Owner access to delete the workspace." : undefined} className={isViewer ? "cursor-not-allowed inline-block" : "inline-block"}>
                <button
                  data-testid="button-delete-workspace"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isViewer}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  <Trash2 size={12} />
                  Delete Workspace
                </button>
              </span>
            ) : (
              <div className="space-y-3 p-3 bg-red-950/30 border border-red-500/25 rounded-lg">
                <p className="text-[11px] text-red-300">Type <span className="font-mono bg-red-500/15 px-1 py-0.5 rounded">{workspace?.name}</span> to confirm:</p>
                <input
                  data-testid="input-delete-confirm-name"
                  value={deleteConfirmName}
                  onChange={(e) => setDeleteConfirmName(e.target.value)}
                  placeholder={workspace?.name}
                  className="w-full bg-muted/50 border border-red-500/30 rounded-lg px-3 py-2 text-xs focus:border-red-500 outline-none text-foreground"
                  onKeyDown={(e) => e.key === "Enter" && handleDeleteWorkspace()}
                />
                <div className="flex items-center gap-2">
                  <button
                    data-testid="button-confirm-delete-workspace"
                    onClick={handleDeleteWorkspace}
                    disabled={deleting || deleteConfirmName.trim().toLowerCase() !== workspace?.name?.trim().toLowerCase()}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-red-600/80 text-white rounded-lg text-xs font-medium hover:bg-red-600 transition-colors disabled:opacity-40"
                  >
                    {deleting ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                    Delete
                  </button>
                  <button
                    onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmName(""); }}
                    className="text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

        <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-4" data-testid="section-advanced-settings">
          <div className="flex items-center gap-3">
            <Code2 size={18} className="text-muted-foreground" />
            <h2 className="font-display font-bold text-foreground">Advanced</h2>
          </div>
          <p className="text-xs text-muted-foreground/70">Technical details for API integrations and support.</p>
          
          <div className="p-3 rounded-xl bg-muted/30 border border-border space-y-1.5">
            <label className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold block">Workspace Public ID (UUID)</label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[11px] font-mono text-muted-foreground/80 bg-background/50 px-2 py-1 rounded border border-border">
                {workspace?.publicId || "Generating..."}
              </code>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7"
                onClick={() => {
                  if (workspace?.publicId) {
                    navigator.clipboard.writeText(workspace.publicId);
                    toast({ title: "Copied to clipboard" });
                  }
                }}
                data-testid="button-copy-workspace-uuid"
              >
                <Copy size={12} />
              </Button>
            </div>
          </div>
        </div>
    </div>
  );
}

function humanizeFeature(f: string): string | null {
  const [key, val] = f.split(':');
  if (val === 'disabled' || val === '0') return null;
  switch (key) {
    case 'mcp': return val === 'enabled' ? 'External Agent API (MCP)' : null;
    case 'pete': return val === 'enabled' ? 'Pete Ad Manager' : null;
    case 'rogers': return val === 'enabled' ? 'Rogers CRM' : null;
    case 'video': return val === 'enabled' ? 'Video / Reels content' : null;
    case 'priority_support': return val === 'enabled' ? 'Priority support' : null;
    case 'trial_credits': return parseInt(val) > 0 ? `${val} trial AI credits` : null;
    case 'trial_days': return parseInt(val) > 0 ? `${val}-day trial period` : null;
    default: return null;
  }
}

function SubscriptionSection({ workspace }: { workspace?: Workspace }) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [openingPortal, setOpeningPortal] = useState(false);

  const { data: credits } = useQuery<{ balance: number; allowance: number; overageCap: number | null; tokens?: { used: number; allowance: number; resetsOn: string } | null }>({
    queryKey: ["/api/credits"],
  });

  const { data: packages = [] } = useQuery<any[]>({
    queryKey: ["/api/packages"],
  });

  const plan = (user as any)?.subscription || "free";
  const DELINQUENT_STATUSES = ["free", "free_trial", "past_due", "unpaid", "canceled", ""];
  const hasPlan = !!plan && !DELINQUENT_STATUSES.includes(plan);

  async function startCheckout(packageId: number) {
    setUpgrading(String(packageId));
    try {
      const res = await fetch("/api/stripe/checkout", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packageId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast({ title: "Checkout unavailable", description: data.message || "Could not start checkout.", variant: "destructive" });
        setUpgrading(null);
      }
    } catch {
      toast({ title: "Checkout error", variant: "destructive" });
      setUpgrading(null);
    }
  }

  async function openPortal() {
    setOpeningPortal(true);
    try {
      const res = await fetch("/api/stripe/portal", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
      const data = await res.json();
      if (data.url) {
        window.open(data.url, "_blank");
      } else {
        toast({ title: "Could not open billing portal", description: data.message || "Please try again.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Failed to open billing portal", variant: "destructive" });
    } finally {
      setOpeningPortal(false);
    }
  }

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-subscription">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CreditCard size={20} className="text-emerald-400" />
          <h2 className="font-display font-bold text-foreground">Subscription & Billing</h2>
        </div>
        <Button
          onClick={openPortal}
          disabled={openingPortal}
          variant="outline"
          size="sm"
          className="gap-2 text-xs"
          data-testid="button-manage-subscription"
        >
          {openingPortal ? <Loader2 size={12} className="animate-spin" /> : <CreditCard size={12} />}
          Manage Billing
        </Button>
      </div>

      {hasPlan && credits && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-muted/30 border border-border rounded-xl p-4">
            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Current Plan</span>
            <span className="text-lg font-bold text-foreground capitalize" data-testid="text-current-plan">{plan}</span>
          </div>
          {credits.tokens && credits.tokens.allowance > 0 ? (
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">AI used this month</span>
              <span className="text-lg font-bold text-emerald-400" data-testid="text-token-usage">{Math.min(100, Math.round((credits.tokens.used / credits.tokens.allowance) * 100))}%</span>
              <p className="text-[10px] text-muted-foreground/60 mt-0.5">{credits.tokens.used.toLocaleString()} of {credits.tokens.allowance.toLocaleString()} tokens · resets {credits.tokens.resetsOn}</p>
            </div>
          ) : (
            <div className="bg-muted/30 border border-border rounded-xl p-4">
              <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Credits Remaining</span>
              <span className="text-lg font-bold text-emerald-400" data-testid="text-credit-balance">{credits.balance ?? 0} / {credits.allowance ?? 0}</span>
            </div>
          )}
          {user && (user as any).trialCreditsGranted > 0 && (
            <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4" data-testid="card-trial-credits">
              <span className="text-[10px] text-blue-400 uppercase tracking-wider block mb-1 font-bold">Trial Credits</span>
              <div className="space-y-0.5">
                <p className="text-lg font-bold text-blue-400">{(user as any).trialCreditsGranted} remaining</p>
                {(user as any).trialExpiresAt && (
                  <p className="text-[10px] text-blue-400/60">Expires: {new Date((user as any).trialExpiresAt).toLocaleDateString()}</p>
                )}
              </div>
            </div>
          )}
          <div className="bg-muted/30 border border-border rounded-xl p-4">
            <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wider block mb-1">Overage Cap</span>
            <span className="text-lg font-bold text-foreground" data-testid="text-overage-cap">{credits.overageCap != null ? `$${credits.overageCap}` : "None set"}</span>
          </div>
        </div>
      )}

        {workspace && (
          <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 space-y-3" data-testid="section-workspace-budget">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-blue-400 uppercase tracking-wider font-bold">Monthly Workspace AI Budget</span>
              <span className="text-xs font-mono text-blue-400">
                ${((workspace.currentMonthSpendCents || 0) / 100).toFixed(2)} / ${((workspace.monthlyBudgetCents || 500) / 100).toFixed(2)}
              </span>
            </div>
            <div className="h-1.5 w-full bg-blue-500/10 rounded-full overflow-hidden">
              <div 
                className="h-full bg-blue-500 transition-all duration-500" 
                style={{ width: `${Math.min(100, ((workspace.currentMonthSpendCents || 0) / (workspace.monthlyBudgetCents || 500)) * 100)}%` }}
              />
            </div>
            <p className="text-[10px] text-muted-foreground/60 italic">
              This is the shared budget for all AI operations in this workspace. Resetting on {workspace.budgetResetAt ? new Date(workspace.budgetResetAt).toLocaleDateString() : 'the 1st of the month'}.
            </p>
          </div>
        )}

      {!hasPlan && packages.length > 0 && (
        <div>
          <p className="text-xs text-muted-foreground/70 mb-4">Choose a plan to unlock all AI agents, Draper's Radar, team collaboration, and more.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {packages.map((pkg: any) => {
              const planLabels = (pkg.features || []).map(humanizeFeature).filter(Boolean) as string[];
              return (
              <div
                key={pkg.id}
                className="bg-muted/20 border border-border rounded-xl p-5 space-y-3 relative"
                data-testid={`card-plan-${pkg.id}`}
              >
                <h3 className="font-display font-bold text-foreground">{pkg.name}</h3>
                {pkg.description && <p className="text-xs text-muted-foreground/70">{pkg.description}</p>}
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-foreground">${pkg.price}</span>
                  <span className="text-xs text-muted-foreground">/{pkg.billingCycle === "yearly" ? "yr" : "mo"}</span>
                </div>
                {planLabels.length > 0 && (
                  <ul className="space-y-1.5 text-xs text-muted-foreground">
                    {planLabels.map((label) => <li key={label}>{label}</li>)}
                  </ul>
                )}
                {pkg.features?.some((f: string) => f === "mcp:enabled") && (
                  <div className="bg-blue-500/5 border border-blue-500/15 rounded-lg p-3 text-[11px] text-blue-400/70 leading-relaxed">
                    Your AI agents connect via the API key in <strong className="text-blue-400">Settings → Connect Your Agent</strong>. Gemini usage is disabled on this plan — bring your own AI (Claude, GPT, n8n, OpenClaw).
                  </div>
                )}
                <Button
                  onClick={() => startCheckout(pkg.id)}
                  disabled={!!upgrading || !pkg.stripePriceId}
                  className="w-full"
                  data-testid={`button-subscribe-${pkg.id}`}
                >
                  {upgrading === String(pkg.id) ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                  {pkg.stripePriceId ? `Get ${pkg.name}` : "Coming Soon"}
                </Button>
              </div>
              );
            })}
          </div>
        </div>
      )}

      {!hasPlan && packages.length === 0 && (
        <p className="text-xs text-muted-foreground/60">No subscription plans are available yet. Contact your administrator.</p>
      )}

      <InvoiceList />
    </div>
  );
}

function InvoiceList() {
  const { data, isLoading } = useQuery<{ invoices: any[] }>({
    queryKey: ["/api/stripe/invoices"],
  });
  const invoices = data?.invoices || [];

  if (isLoading || invoices.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid="section-invoices">
      <h3 className="text-xs font-semibold text-muted-foreground/60 uppercase tracking-wider mb-3">Billing History</h3>
      <div className="space-y-2">
        {invoices.map((inv: any) => (
          <div key={inv.id} className="flex items-center justify-between gap-3 py-2 px-3 bg-muted/20 border border-border rounded-lg hover:bg-muted/30 transition-colors" data-testid={`invoice-row-${inv.id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${inv.status === "paid" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : inv.status === "open" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "bg-muted/40 text-muted-foreground border border-border"}`}>
                {inv.status}
              </span>
              <span className="text-xs text-muted-foreground truncate">{inv.description || `Invoice ${inv.number || inv.id.slice(-8)}`}</span>
              <span className="text-xs text-muted-foreground/40 shrink-0">{new Date(inv.created * 1000).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-sm font-bold text-foreground">${((inv.amountPaid || inv.amountDue) / 100).toFixed(2)}</span>
              <div className="flex items-center gap-1">
                {inv.hostedUrl && (
                  <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:text-blue-300 uppercase tracking-wider" data-testid={`link-invoice-view-${inv.id}`}>View</a>
                )}
                {inv.pdfUrl && (
                  <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer" className="text-[10px] text-muted-foreground/40 hover:text-muted-foreground uppercase tracking-wider ml-2" data-testid={`link-invoice-pdf-${inv.id}`}>PDF</a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailOutboxSection({ workspaceId }: { workspaceId: number | null }) {
  const { toast } = useToast();
  const [outboxTab, setOutboxTab] = useState<"pending" | "sent" | "all">("pending");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editSubject, setEditSubject] = useState("");
  const [editBody, setEditBody] = useState("");

  const { data: emails, isLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", workspaceId, "email-outbox"],
    queryFn: async () => {
      if (!workspaceId) return [];
      const res = await fetch(`/api/workspaces/${workspaceId}/email-outbox`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!workspaceId,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, subject, body }: { id: number; subject: string; body: string }) => {
      const res = await apiRequest("PATCH", `/api/workspaces/${workspaceId}/email-outbox/${id}`, { subject, body });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "email-outbox"] });
      setEditingId(null);
      toast({ title: "Email updated", description: "Your changes have been saved." });
    },
    onError: () => {
      toast({ title: "Update failed", description: "Could not save changes.", variant: "destructive" });
    },
  });

  const startEdit = (email: any) => {
    setEditingId(email.id);
    setEditSubject(email.subject);
    setEditBody(email.body);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = () => {
    if (!editingId) return;
    updateMutation.mutate({ id: editingId, subject: editSubject, body: editBody });
  };

  if (!workspaceId) return null;

  const filteredEmails = (emails || []).filter((e: any) => {
    if (outboxTab === "pending") return e.status !== "SENT" && e.status !== "FAILED";
    if (outboxTab === "sent") return e.status === "SENT";
    return true;
  });

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-email-outbox">
      <div className="flex items-center gap-3 mb-2">
        <Mail size={20} className="text-purple-400" />
        <h2 className="font-display font-bold text-foreground">Email Outbox</h2>
      </div>
      <p className="text-xs text-muted-foreground/70">AI-generated nurture email drafts queued from Lead Desk. No SMTP provider is connected — drafts are stored here for review and manual sending.</p>

      <div className="flex gap-1 bg-muted/30 rounded-lg p-1 w-fit" data-testid="outbox-tabs">
        {(["pending", "sent", "all"] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setOutboxTab(tab)}
            className={`text-xs font-bold px-3 py-1.5 rounded-md transition-all capitalize ${outboxTab === tab ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            data-testid={`tab-outbox-${tab}`}
          >
            {tab === "pending" ? "Pending" : tab === "sent" ? "Sent" : "All"}
            <span className="ml-1.5 text-[10px] opacity-60">
              {tab === "pending" ? (emails || []).filter((e: any) => e.status !== "SENT" && e.status !== "FAILED").length
                : tab === "sent" ? (emails || []).filter((e: any) => e.status === "SENT").length
                : (emails || []).length}
            </span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground/70 text-sm py-4">
          <Loader2 size={14} className="animate-spin" />
          Loading emails...
        </div>
      ) : filteredEmails.length > 0 ? (
        <div className="space-y-3 max-h-[500px] overflow-y-auto">
          {filteredEmails.map((email: any) => {
            const isPending = email.status !== "SENT" && email.status !== "FAILED";
            const isEditing = editingId === email.id;
            return (
              <div key={email.id} className="bg-muted/30 border border-border rounded-xl p-4 space-y-2" data-testid={`email-outbox-item-${email.id}`}>
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="space-y-1 flex-1 min-w-0">
                    {isEditing ? (
                      <input
                        value={editSubject}
                        onChange={(e) => setEditSubject(e.target.value)}
                        className="w-full text-sm font-bold bg-muted/50 border border-border rounded-md px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-purple-500/50"
                        data-testid={`input-edit-subject-${email.id}`}
                      />
                    ) : (
                      <p className="text-sm font-bold text-foreground truncate">{email.subject}</p>
                    )}
                    <p className="text-xs text-muted-foreground">To: {email.toName ? `${email.toName} <${email.toEmail}>` : email.toEmail}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {isPending && !isEditing && (
                      <button
                        onClick={() => startEdit(email)}
                        className="text-[10px] font-bold px-2 py-1 rounded-md bg-muted/50 border border-border text-muted-foreground hover:text-foreground hover:border-purple-500/50 transition-colors flex items-center gap-1"
                        data-testid={`button-edit-email-${email.id}`}
                      >
                        <Pencil size={10} /> Edit
                      </button>
                    )}
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase ${
                      email.status === "SENT" ? "bg-green-500/10 text-green-400" :
                      email.status === "FAILED" ? "bg-red-500/10 text-red-400" :
                      "bg-yellow-500/10 text-yellow-400"
                    }`} data-testid={`email-status-${email.id}`}>
                      {email.status}
                    </span>
                  </div>
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editBody}
                      onChange={(e) => setEditBody(e.target.value)}
                      rows={6}
                      className="w-full text-xs bg-muted/50 border border-border rounded-md px-2 py-1.5 text-foreground/80 focus:outline-none focus:ring-1 focus:ring-purple-500/50 resize-none font-mono"
                      data-testid={`input-edit-body-${email.id}`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={saveEdit}
                        disabled={updateMutation.isPending}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30 transition-colors flex items-center gap-1 disabled:opacity-50"
                        data-testid={`button-save-email-${email.id}`}
                      >
                        {updateMutation.isPending ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="text-xs font-bold px-3 py-1.5 rounded-lg bg-muted/50 text-muted-foreground border border-border hover:text-foreground transition-colors"
                        data-testid={`button-cancel-edit-${email.id}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground/70 line-clamp-3 whitespace-pre-wrap">{email.body}</p>
                )}

                <div className="flex items-center gap-3 text-[10px] text-muted-foreground/50">
                  <span>Queued {new Date(email.createdAt).toLocaleString()}</span>
                  {email.sentAt && (
                    <span className="text-green-400/70" data-testid={`sent-at-${email.id}`}>
                      Sent {new Date(email.sentAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="py-6 text-center text-muted-foreground/50 text-sm">
          {outboxTab === "pending" ? "No pending emails. Emails queued by Rogers will appear here." :
           outboxTab === "sent" ? "No sent emails yet." :
           "No emails yet. Use Lead Desk to generate nurture emails via Rogers."}
        </div>
      )}
    </div>
  );
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function CalendarBookingSection({ workspaceId, workspace, isViewer }: { workspaceId: number | null; workspace: Workspace | undefined; isViewer?: boolean }) {
  const { toast } = useToast();
  const [copiedLink, setCopiedLink] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);

  const bookingSettings = (workspace?.bookingSettings as any) || {
    enabled: false,
    durationMins: 30,
    bufferMins: 15,
    timezone: "America/New_York",
    workingHours: { start: "09:00", end: "17:00", days: [1, 2, 3, 4, 5] },
    meetingTitle: "Discovery Call",
    meetingDescription: "Let's discuss how we can help your business grow.",
  };

  const [bookingForm, setBookingForm] = useState(bookingSettings);
  const [bookingInitialized, setBookingInitialized] = useState<number | null>(null);

  useEffect(() => {
    if (workspace && workspace.id !== bookingInitialized) {
      const bs = (workspace.bookingSettings as any) || bookingSettings;
      setBookingForm({
        ...bs, // keep the settings this form does not show (minimum notice, horizon, daily cap) when it saves
        enabled: bs.enabled ?? false,
        durationMins: bs.durationMins ?? 30,
        bufferMins: bs.bufferMins ?? 15,
        timezone: bs.timezone ?? "America/New_York",
        workingHours: {
          start: bs.workingHours?.start ?? "09:00",
          end: bs.workingHours?.end ?? "17:00",
          days: bs.workingHours?.days ?? [1, 2, 3, 4, 5],
        },
        meetingTitle: bs.meetingTitle ?? "Discovery Call",
        meetingDescription: bs.meetingDescription ?? "",
        externalBookingUrl: bs.externalBookingUrl ?? "",
        notifyEmail: bs.notifyEmail ?? "",
      });
      setBookingInitialized(workspace.id);
    }
  }, [workspace, bookingInitialized]);

  const { data: calendarIntegrations, isLoading: calLoading } = useQuery<any[]>({
    queryKey: ["/api/workspaces", workspaceId, "calendar-integrations"],
    queryFn: async () => {
      if (!workspaceId) return [];
      const res = await fetch(`/api/workspaces/${workspaceId}/calendar-integrations`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!workspaceId,
  });

  const disconnectCalMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/workspaces/${workspaceId}/calendar-integrations/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "calendar-integrations"] });
      toast({ title: "Calendar disconnected" });
    },
    onError: (err: any) => {
      toast({ title: "Disconnect failed", description: err.message, variant: "destructive" });
    },
  });

  async function connectCalendar(provider: string) {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/calendar/authorize/${provider}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        toast({ title: "Failed to start calendar connection", variant: "destructive" });
      }
    } catch {
      toast({ title: "Failed to connect calendar", variant: "destructive" });
    }
  }

  async function saveBookingSettings() {
    setSavingBooking(true);
    try {
      await apiRequest("PUT", `/api/workspaces/${workspaceId}`, { bookingSettings: bookingForm });
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
      toast({ title: "Booking settings saved" });
    } catch {
      toast({ title: "Failed to save booking settings", variant: "destructive" });
    } finally {
      setSavingBooking(false);
    }
  }

  function toggleDay(day: number) {
    const days = bookingForm.workingHours.days.includes(day)
      ? bookingForm.workingHours.days.filter((d: number) => d !== day)
      : [...bookingForm.workingHours.days, day].sort();
    setBookingForm({ ...bookingForm, workingHours: { ...bookingForm.workingHours, days } });
  }

  if (!workspaceId) return null;

  const bookingUrl = `${window.location.origin}/book/${workspace?.bookingSlug || workspaceId}`;

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-calendar-booking">
      <div className="flex items-center gap-3 mb-2">
        <Calendar size={20} className="text-blue-400" />
        <h2 className="font-display font-bold text-foreground">Calendar & Booking</h2>
      </div>
      <p className="text-xs text-muted-foreground/70">Connect your calendar, configure booking settings, and share your public booking link with leads.</p>

      <div className="space-y-4">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Calendar Connections</h3>

        {calLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground/70 text-sm py-2">
            <Loader2 size={14} className="animate-spin" />
            Loading calendars...
          </div>
        ) : calendarIntegrations && calendarIntegrations.length > 0 ? (
          <div className="space-y-2">
            {calendarIntegrations.map((ci: any) => (
              <div key={ci.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-green-500/5 border border-green-500/15 flex-wrap" data-testid={`calendar-connection-${ci.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Calendar size={16} className="text-green-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground capitalize" data-testid={`calendar-provider-${ci.id}`}>{ci.provider}</p>
                    {ci.calendarEmail && (
                      <p className="text-[10px] text-muted-foreground/70" data-testid={`calendar-email-${ci.id}`}>{ci.calendarEmail}</p>
                    )}
                  </div>
                </div>
                <button
                  data-testid={`button-disconnect-calendar-${ci.id}`}
                  onClick={() => disconnectCalMutation.mutate(ci.id)}
                  disabled={disconnectCalMutation.isPending}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold bg-muted/50 text-muted-foreground border border-border hover:border-red-500/30 hover:text-red-400 transition-all"
                >
                  <Unlink size={12} />
                  Disconnect
                </button>
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="button-connect-google-calendar"
            onClick={() => connectCalendar("google")}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors"
          >
            <SiGoogle size={14} />
            Connect Google Calendar
          </button>
          <button
            data-testid="button-connect-microsoft-calendar"
            onClick={() => connectCalendar("microsoft")}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors"
          >
            <Calendar size={14} />
            Connect Microsoft Outlook
          </button>
        </div>
      </div>

      <div className="border-t border-border pt-5 space-y-4">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Booking Settings</h3>

        <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-border">
          <div>
            <p className="text-sm font-medium text-foreground">Enable Booking</p>
            <p className="text-[10px] text-muted-foreground/60">Allow leads to book appointments via your public link</p>
          </div>
          <button
            data-testid="toggle-booking-enabled"
            onClick={() => setBookingForm({ ...bookingForm, enabled: !bookingForm.enabled })}
            className={`relative w-11 h-6 rounded-full transition-colors ${bookingForm.enabled ? "bg-blue-600" : "bg-muted"}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${bookingForm.enabled ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Meeting Duration</label>
            <select
              data-testid="select-meeting-duration"
              value={bookingForm.durationMins}
              onChange={(e) => setBookingForm({ ...bookingForm, durationMins: Number(e.target.value) })}
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground appearance-none"
            >
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Buffer Between Meetings</label>
            <select
              data-testid="select-buffer-mins"
              value={bookingForm.bufferMins}
              onChange={(e) => setBookingForm({ ...bookingForm, bufferMins: Number(e.target.value) })}
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground appearance-none"
            >
              <option value={0}>No buffer</option>
              <option value={5}>5 minutes</option>
              <option value={10}>10 minutes</option>
              <option value={15}>15 minutes</option>
              <option value={30}>30 minutes</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Start Time</label>
            <input
              data-testid="input-working-hours-start"
              type="time"
              value={bookingForm.workingHours.start}
              onChange={(e) => setBookingForm({ ...bookingForm, workingHours: { ...bookingForm.workingHours, start: e.target.value } })}
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">End Time</label>
            <input
              data-testid="input-working-hours-end"
              type="time"
              value={bookingForm.workingHours.end}
              onChange={(e) => setBookingForm({ ...bookingForm, workingHours: { ...bookingForm.workingHours, end: e.target.value } })}
              className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
            />
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Working Days</label>
          <div className="flex flex-wrap gap-2" data-testid="working-days-checkboxes">
            {DAY_LABELS.map((label, idx) => (
              <label
                key={idx}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all text-xs font-medium ${
                  bookingForm.workingHours.days.includes(idx)
                    ? "bg-blue-600/20 border-blue-500/30 text-blue-400"
                    : "bg-muted/30 border-border text-muted-foreground"
                }`}
              >
                <input
                  type="checkbox"
                  checked={bookingForm.workingHours.days.includes(idx)}
                  onChange={() => toggleDay(idx)}
                  className="accent-blue-500 w-3 h-3"
                  data-testid={`checkbox-day-${idx}`}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Timezone</label>
          <input
            data-testid="input-timezone"
            value={bookingForm.timezone}
            onChange={(e) => setBookingForm({ ...bookingForm, timezone: e.target.value })}
            placeholder="America/New_York"
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Meeting Title</label>
          <input
            data-testid="input-meeting-title"
            value={bookingForm.meetingTitle}
            onChange={(e) => setBookingForm({ ...bookingForm, meetingTitle: e.target.value })}
            placeholder="Discovery Call"
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-2">Meeting Description</label>
          <textarea
            data-testid="input-meeting-description"
            value={bookingForm.meetingDescription}
            onChange={(e) => setBookingForm({ ...bookingForm, meetingDescription: e.target.value })}
            placeholder="Let's discuss how we can help your business grow."
            rows={3}
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none placeholder:text-muted-foreground/50"
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">Send meetings to <span className="text-muted-foreground/50 normal-case font-normal">(optional)</span></label>
          <p className="text-[10px] text-muted-foreground/60 mb-2">Every meeting's notice and calendar invite goes to this address (for example the person who takes the calls). Empty = your own email.</p>
          <input
            data-testid="input-booking-notify-email"
            value={bookingForm.notifyEmail || ""}
            onChange={(e) => setBookingForm({ ...bookingForm, notifyEmail: e.target.value })}
            placeholder="manager@yourfirm.com"
            type="email"
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-widest font-bold block mb-1">External Booking URL <span className="text-muted-foreground/50 normal-case font-normal">(optional)</span></label>
          <p className="text-[10px] text-muted-foreground/60 mb-2">Paste your Calendly, Acuity, or other booking page URL. Rogers will use this link in outreach emails instead of the in-app booking page.</p>
          <input
            data-testid="input-external-booking-url"
            value={bookingForm.externalBookingUrl || ""}
            onChange={(e) => setBookingForm({ ...bookingForm, externalBookingUrl: e.target.value })}
            placeholder="https://calendly.com/yourname/discovery-call"
            type="url"
            className="w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-muted-foreground/50"
          />
          {bookingForm.externalBookingUrl && (
            <p className="text-[10px] text-emerald-400/80 mt-1.5">Rogers will use this URL in all outreach emails when inviting leads to book.</p>
          )}
        </div>

        <button
          data-testid="button-save-booking-settings"
          onClick={saveBookingSettings}
          disabled={savingBooking || isViewer}
          title={isViewer ? "You need Editor or Owner access." : undefined}
          className="flex items-center gap-2 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50 disabled:pointer-events-none"
        >
          {savingBooking ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Booking Settings
        </button>
      </div>

      <div className="border-t border-border pt-5 space-y-3">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Public Booking Link</h3>
        {bookingForm.externalBookingUrl ? (
          <div className="space-y-2">
            <p className="text-[10px] text-emerald-400/80">Rogers is using your external booking URL below. The in-app booking page is still available at:</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-muted/50 border border-border rounded-xl px-4 py-3 opacity-60">
                <Link2 size={14} className="text-muted-foreground shrink-0" />
                <span className="text-sm text-muted-foreground font-mono truncate" data-testid="text-booking-page-url">{bookingUrl}</span>
              </div>
              <button
                data-testid="button-copy-booking-url"
                onClick={() => { navigator.clipboard.writeText(bookingUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }}
                className="flex items-center gap-1.5 px-4 py-3 bg-muted/50 text-muted-foreground border border-border rounded-xl text-sm font-medium hover:bg-muted/70 transition-colors"
              >
                {copiedLink ? <Check size={14} /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5">
            <p className="text-[10px] text-muted-foreground/60">Rogers will include this link in outreach emails when inviting leads to book a call.</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-muted/50 border border-border rounded-xl px-4 py-3">
                <Link2 size={14} className="text-blue-400 shrink-0" />
                <span className="text-sm text-muted-foreground font-mono truncate" data-testid="text-booking-page-url">{bookingUrl}</span>
              </div>
              <button
                data-testid="button-copy-booking-url"
                onClick={() => { navigator.clipboard.writeText(bookingUrl); setCopiedLink(true); setTimeout(() => setCopiedLink(false), 2000); }}
                className="flex items-center gap-1.5 px-4 py-3 bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-xl text-sm font-medium hover:bg-blue-600/30 transition-colors"
              >
                {copiedLink ? <Check size={14} /> : <Copy size={14} />}
                {copiedLink ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}